<template>
  <div
    ref="stageContainer"
    class="relative w-full h-full"
    :class="{
      'cursor-move': selectedTool === SELECTABLE_TOOLS.MOVE_GRID,
    }"
    :style="{
      cursor: resizeCursor || undefined,
      touchAction: 'none',
      overscrollBehavior: 'none'
    }"
    tabindex="0"
  >
    <!-- HTML Mobile Resize Handles - Positioned above Konva stage -->
    <div
      v-if="isTouchDevice && showMobileResizeHandles"
      class="mobile-handles-overlay"
      style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;"
    >
      <!-- Right Edge Handle -->
      <div
        class="mobile-handle right-handle"
        :style="rightEdgeHandleStyle"
        @touchstart="(e) => handleMobileHandleTouchStart(e, ['right'])"
        @touchmove="handleMobileHandleTouchMove"
        @touchend="handleMobileHandleTouchEnd"
        @click.stop.prevent
        @mousedown.stop.prevent
        style="pointer-events: auto;"
      ></div>

      <!-- Bottom Edge Handle -->
      <div
        class="mobile-handle bottom-handle"
        :style="bottomEdgeHandleStyle"
        @touchstart="(e) => handleMobileHandleTouchStart(e, ['bottom'])"
        @touchmove="handleMobileHandleTouchMove"
        @touchend="handleMobileHandleTouchEnd"
        @click.stop.prevent
        @mousedown.stop.prevent
        style="pointer-events: auto;"
      ></div>

      <!-- Corner Handle -->
      <div
        class="mobile-handle corner-handle"
        :style="cornerHandleStyle"
        @touchstart="(e) => handleMobileHandleTouchStart(e, ['right', 'bottom'])"
        @touchmove="handleMobileHandleTouchMove"
        @touchend="handleMobileHandleTouchEnd"
        @click.stop.prevent
        @mousedown.stop.prevent
        style="pointer-events: auto;"
      ></div>
    </div>

    <v-stage
      ref="stageRef"
      :config="stageConfig"
      :tabindex="1"
      :draggable="selectedTool === SELECTABLE_TOOLS.MOVE_GRID"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
      @mouseenter="handleMouseEnter"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @wheel="handleWheel"
      @click="handleStageClick"
      @tap="handleStageTap"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <CanvasGridPatternLayer v-if="gridPattern" />
      <v-layer :config="{ id: 'temp-item-layer', name: 'temp-item-layer' }">
        <v-group
          v-if="showTempShapes"
          ref="tempGroupRef"
          @click="handleTempGroupClick"
        >
          <template v-if="showDepthPreview">
            <v-group
              v-for="item in tempNewItems3D"
              :key="item.id"
            >
              <component
                v-for="face in item.faces"
                :is="getShapeComponent(face.type)"
                :key="face.id"
                :config="face"
              />
            </v-group>
          </template>
          <component
            v-else
            :is="getShapeComponent(item.type)"
            v-for="item in tempNewItems"
            :key="item.id"
            :config="item"
          />
        </v-group>
        <!--
          Auto-expand candidate ghosts: low-opacity outlines at every
          valid drop position. `listening: false` so they don't
          intercept clicks; the actual temp group above handles input.
        -->
        <!--
          Auto-expand candidate ghosts: disabled — the live auto-grow
          alone is enough to help the user. Kept here as a comment so the
          dropCandidates state still exists if we want to bring it back.
        -->
        <!-- <v-group
          v-if="showTempShapes && dropCandidates.length"
          id="candidate-ghosts-layer"
        >
          <v-rect
            v-for="(cand, i) in dropCandidates"
            :key="`cand-${i}`"
            :config="{
              x: cand.x,
              y: cand.y,
              width: cand.w,
              height: cand.h,
              fill: 'rgba(74, 144, 226, 0.08)',
              stroke: 'rgba(74, 144, 226, 0.35)',
              strokeWidth: 1,
              dash: [6, 4],
              listening: false,
            }"
          />
        </v-group> -->
      </v-layer>
      <v-layer
        ref="shapesLayerRef"
        :config="{ id: 'shapes-layer', name: 'shapes-layer' }"
      >
        <template v-if="showDepthPreview">
          <v-group
            v-for="item in shapeList3DSorted"
            :key="item.id"
            :config="{
              id: item.id,
              depth: item.depth,
              dragDistance: Math.min(gridPattern?.snap?.x ?? 30, gridPattern?.snap?.y ?? 30)
            }"
            :draggable="![SELECTABLE_TOOLS.MOVE_GRID, SELECTABLE_TOOLS.LASSO, SELECTABLE_TOOLS.RECT_SELECT].includes(selectedTool)"
            @dragstart="handleDragStart"
            @dragend="handleDragEnd"
            @dragmove="handleDragMove"
            @mouseenter="handleShapeMouseEnter"
            @mouseleave="handleShapeMouseLeave"
            @click="handleSelect"
            @tap="handleTap"
          >
            <component
              v-for="face in item.faces"
              :is="getShapeComponent(face.type)"
              :key="face.id"
              :config="face"
            />
          </v-group>
        </template>

        <component
          v-else
          :is="getShapeComponent(shape.type)"
          v-for="shape in shapeList"
          :key="shape.id"
          :config="{
            ...shape,
            dragDistance: Math.min(gridPattern?.snap?.x ?? 30, gridPattern?.snap?.y ?? 30)
          }"
          :draggable="![SELECTABLE_TOOLS.MOVE_GRID, SELECTABLE_TOOLS.LASSO, SELECTABLE_TOOLS.RECT_SELECT].includes(selectedTool)"
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
          @dragmove="handleDragMove"
          @mouseenter="handleShapeMouseEnter"
          @mouseleave="handleShapeMouseLeave"
          @click="handleSelect"
          @tap="handleTap"
        />

        <!-- 3D labels (only shown for shapes not covered by shapes on top) -->
        <template v-if="gridPattern?.id === GRID_PATTERN_IDS.THREE_D && !showDepthPreview">
          <v-group v-for="shape in shapesWithVisibleLabels" :key="`${shape.id}-label`">
            <v-text
              v-for="label in getLabelConfigs(shape)"
              :key="label.id"
              :config="label"
            />
          </v-group>
        </template>

        <!-- DEBUG: 3D Z-Order Visualization -->
        <template v-if="DEBUG_3D_SORT && showDepthPreview">
          <template v-for="label in debug3DLabels" :key="label.id">
            <v-rect
              v-if="label._isRect"
              :config="label"
            />
            <v-text
              v-else
              :config="label"
            />
          </template>
        </template>
      </v-layer>
      <!-- Selection layer - always present to avoid DOM manipulation issues -->
      <v-layer
        :config="{ id: 'selection-layer', name: 'selection-layer' }"
      >
        <v-line
          v-if="lassoSelectionItem"
          :config="lassoSelectionItem"
        />
        <v-rect
          v-if="rectSelectionItem"
          :config="rectSelectionItem"
        />
        <!-- Rotate button for selected items with rotation options -->
        <v-group
          v-if="showRotateButton && rotateButtonConfig"
          :config="rotateButtonConfig"
          @click="handleRotateClick"
          @tap="handleRotateClick"
          @mouseover="e => e.target.getStage().container().style.cursor = 'pointer'"
          @mouseout="e => e.target.getStage().container().style.cursor = 'default'"
        >
          <v-circle
            :config="{
              radius: isTouchDevice ? 24 : 16,
              fill: 'white',
              stroke: '#313558',
              strokeWidth: 2,
            }"
          />
          <!-- Custom-drawn rotation icon using Konva Arrow (↺ reference) -->
          <v-arrow
            :config="rotationIconConfig"
          />
        </v-group>
      </v-layer>
      <!-- Grid Resize Indicators - Desktop (hover-based) -->
      <v-layer
        v-if="!isTouchDevice && hoveredEdges.length > 0"
        :config="{ id: 'resize-indicators-layer', name: 'resize-indicators-layer' }"
      >
        <v-line
          v-if="hoveredEdges.includes('right')"
          :config="rightEdgeIndicator"
        />
        <v-line
          v-if="hoveredEdges.includes('bottom')"
          :config="bottomEdgeIndicator"
        />
      </v-layer>
    </v-stage>
    <CanvasScrollbars
      :stage-size="canvasStageSize"
      :content-size="canvasContentSize"
    />
  </div>
</template>

<script setup>
// import Konva from "konva"
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, triggerRef } from "vue"
import { GRID_PATTERN_IDS, INCH_IN_PIXELS, MAX_GRID_HEIGHT_PX, MAX_GRID_WIDTH_PX, SELECTABLE_TOOLS } from "~/composables/grid/constants"
import { transformShapeForAdd } from '~/composables/shapes/transform'
import { rotateSelectionClockwise } from '~/composables/grid/rotateSelection'
import { getParentShape, getShapeById } from '~/composables/shapes/utils'
import { isWoodSlatShapeId, snapWoodSlatPosition } from '~/composables/shapes/woodSlats'
import { isLayeredShape } from '~/shared/layeredEngraving'
import { Toast } from "~/composables/tools"
import { findValidDropCandidates } from "~/composables/grid/placement"
import { CollisionLevel } from "~/types/grid.types"
import {
  boundingBoxIntersects,
  calculateMobileCenterPosition,
  calculateMobileZoom,
  checkRotationCollision,
  checkShapeIntersection,
  findNearbyNonCollidingPosition,
  get3dShapeGroup,
  getBoxCorners,
  getCenter,
  getColorFromLabel,
  getContrastingTextColor,
  getDistance,
  getFrontFaceFromGroup,
  getResizeCursor,
  getRotatedPosition,
  getShapeComponent,
  getShapePoints,
  isPointInPolygon,
  moveItemsByLeaderDelta,
  resolveBlankCanvasClickTool,
  sort3dShapeList
} from "./helpers"
import CanvasScrollbars from './CanvasScrollbars.vue'
import { useCanvasScrollbars } from '~/composables/useCanvasScrollbars'
const props = defineProps({
  returnToAddOnBlankClick: {
    type: Boolean,
    default: false,
  },
  // Draw the grid past the bottom of the container so it runs under the
  // mobile sheet's top edge. Only the mobile shell sets this - the desktop
  // shell has no sheet, and there the canvas must stay exactly the size of
  // its container.
  extendBehindSheet: {
    type: Boolean,
    default: false,
  },
})
const store = useGridStore();
const projectStore = useProjectStore();
const modalStore = useModalStore();
const { closeShapeGroup } = useShapeInteractions();
const { isTouchDevice, isMobile, touchTargetSize } = useDeviceDetection();
const {
  selectedShape,
  selectedColor,
  selectedRotation,
  selectedTool,
  selectedItemsOnGrid,
  tempNewItems,
  lassoSelectionItem,
  rectSelectionItem,
  shapeList,
  shapeList3D,
  startOffset,
  gridPattern,
  placementSnap,
  gridZoom,
  gridPos,
  gridWidth,
  gridHeight,
  undoStack,
  hoveredEdges,
  isResizingGrid,
  showDepthPreview,
} = storeToRefs(store);
const runtimeConfig = useRuntimeConfig();
const stageRef = ref(null);
const shapesLayerRef = ref(null);
const tempGroupRef = ref(null);
const isAnimating = ref(false);
const mouseEvent = ref(null);
const isDrawing = ref(false);
const pathPoints = ref([]);
const lastPoint = ref(null);
const lassoThrottleId = ref(null);
const prevSelectedTool = ref(null);
const isHoldingSelectTool = ref(false);
const keepSelection = ref(true);
const startDragPosition = ref({ x: 0, y: 0 });
const stageContainer = useTemplateRef('stageContainer')
const drawnShapesForCollision = ref([])
const dragCollision = ref(false)
// Auto-expand-grid candidates. Each entry is one valid drop position for
// the current drag/placement, in absolute canvas coordinates. We render
// these as low-opacity outlines in `temp-item-layer` so the user can see
// "where can I put this?" before releasing the mouse. Recomputed
// debounced on cursor move; cleared when not in placement mode.
const dropCandidates = ref([])
let candidateTimer = null

// ============================================================================
// DEBUG: 3D Z-ORDER VISUALIZATION
// Set to true to show debug labels on each 3D shape
// ============================================================================
const DEBUG_3D_SORT = ref(false)

// How far the mobile canvas keeps drawing BEHIND the bottom sheet.
//
// The sheet's top corners are a cutout: the radius leaves the corner
// transparent so the canvas shows through it. But the canvas was clipped to
// the container, whose bottom edge IS the sheet's top edge - so there was
// nothing to see through the cutout, and a flat grey strip was painted
// behind the corners to stand in for it.
//
// Drawing this far past the clip lets the REAL grid run under the sheet's
// top edge, so the corner cutout reveals the grid the user is editing
// instead of a grey wedge. It only has to cover the corner band, which is
// as tall as the sheet's corner radius.
//
// The overflow is added to the STAGE size, not to the layout: the container
// keeps its own height, so nothing about the page layout changes and the
// canvas does not steal pointer events from the sheet (the sheet is a
// separate, higher stacking layer).
const SHEET_OVERFLOW_PX = 40;

// The stage is drawn SHEET_OVERFLOW_PX taller than its container when the
// shell asks for it (the mobile sheet), so the grid continues under the
// sheet's top edge and shows through the rounded corner cutout. The
// desktop stage is exactly the container's size.
//
// The overflow is NOT part of the stage's usable size: the zoom fit and
// the scrollbars both measure the VISIBLE area, so the extra band under
// the sheet must not change how much grid fits on screen.
const stageOverflow = () => (props.extendBehindSheet ? SHEET_OVERFLOW_PX : 0);

const stageConfig = reactive({
  width: stageContainer?.offsetWidth || 960,
  height: stageContainer?.offsetHeight || 720,
  pixelRatio: 1,
  scaleX: gridZoom,
  scaleY: gridZoom,
  dragBoundFunc: handleStageDrag,
  dragDistance: 50,
});

const canvasContentSize = computed(() => ({
  width: gridWidth.value,
  height: gridHeight.value,
}))

// The VISIBLE stage area: the stage minus the band that runs under the
// sheet. The zoom fit and the scrollbars both work in terms of what the
// user can actually see, so they must not count the hidden overflow.
const visibleStageSize = computed(() => ({
  width: stageConfig.width,
  height: stageConfig.height - stageOverflow(),
}))

const canvasStageSize = computed(() => ({
  width: visibleStageSize.value.width,
  height: visibleStageSize.value.height,
}))

const scrollbars = useCanvasScrollbars(canvasStageSize, canvasContentSize)

// Mirror the vertical scrollbar visibility into the store so the studio shell
// can shift the tutorial arrow clear of the scrollbar when it appears.
watch(
  () => scrollbars.visibleY.value,
  (visible) => {
    store.verticalScrollbarVisible = visible
  },
  { immediate: true },
)

onUnmounted(() => {
  store.verticalScrollbarVisible = false
})

const showTempShapes = computed(
  () =>
    [
      SELECTABLE_TOOLS.ADD,
      SELECTABLE_TOOLS.COPY,
      SELECTABLE_TOOLS.CUT,
    ].includes(selectedTool.value) && isAnimating.value && !isResizingGrid.value
)

const resizeCursor = computed(() => {
  if (hoveredEdges.value.length === 0) return null;
  return getResizeCursor(hoveredEdges.value);
})

// Show mobile resize handles when not in other interactive modes
const showMobileResizeHandles = computed(() => {
  const allowedTools = [SELECTABLE_TOOLS.SELECT, SELECTABLE_TOOLS.ADD];
  return allowedTools.includes(selectedTool.value) && !isResizingGrid.value;
})

const somethingChanged = computed((prev) => {
  if (!prev || undoStack.value.length > prev) {
    return undoStack.value.length
  }

  return prev
})

const tempNewItems3D = computed(() => {
  if (!showDepthPreview.value) return []

  if (!tempNewItems.value || tempNewItems.value.length === 0) {
    return []
  }

  return tempNewItems.value.map(item => get3dShapeGroup(item))
})

// Creating a manual comparator for 3D shapes based on their positions
const sort3DbyRect = (ax1, ax2, ay1, ay2, bx1, bx2, by1, by2) => {
  const endsToTheLeft = ax2 <= bx1
  const endsToTheTop = ay2 <= by1
  const endsToTheBottom = ay2 >= by2
  const beginsToTheLeft = ax1 <= bx1
  const beginsToTheRight = ax1 >= bx2
  const beginsToTheTop = ay1 <= by1
  const beginsToTheBottom = ay1 >= by2
  const beginsWithinX = ax1 >= bx1 && ax1 <= bx2
  const beginsWithinY = ay1 >= by1 && ay1 <= by2
  const endsWithinX = ax2 <= bx2 && ax2 >= bx1
  const endsWithinY = ay2 <= by2 && ay2 >= by1

  if (endsToTheLeft && beginsToTheTop && endsWithinY) return 1;
  if (endsToTheLeft && beginsToTheTop && endsToTheBottom) return 1;
  if (beginsWithinY && endsToTheLeft && endsToTheBottom) return 1;
  if (beginsToTheTop && beginsToTheRight && endsWithinY) return -1;
  if (beginsToTheRight && beginsToTheBottom) return -1;
  if (endsToTheLeft && beginsWithinY) return 1;
  if (beginsWithinX && endsToTheTop) return 1;
  if (beginsToTheLeft && endsToTheTop) return 1;
  if (beginsWithinX && beginsToTheBottom) return -1;
  if (beginsToTheBottom && endsWithinX) return -1;
  if (endsToTheLeft && endsToTheTop) return 1;

  // this fix some cases of small gap in top face
  //return (bx1 + by1) - (ax1 + ay1);

  // this fix a relevant scenario of wrong sorting for bottom shapes
  return (bx1 - ax1) - (by1 - ay1);
}
// Computed property that re-sorts shapeList3D during drag based on pending positions
// Uses topological sort with pairwise overlap testing for correct z-ordering
const shapeList3DSorted = computed(() => {
  if (!showDepthPreview.value || !shapeList3D.value || shapeList3D.value.length === 0) {
    return []
  }

  console.log(' ')
  console.log('[       3D SORTED (Topological)      ]')

  // Access the Map's entries to make Vue reactive to triggerRef calls
  const pendingEntries = Array.from(pendingUpdates.value.entries())
  const pendingLookup = Object.fromEntries(pendingEntries)

  // Build shapes with effective center positions for sorting
  const shapesForSort = shapeList3D.value.map(group => {
    const shapeId = group.id.replace('-group', '')
    const shape = shapeList.value.find(s => s.id === shapeId)
    if (!shape) return null

    // During drag: shape center + group offset. No drag: shape center + 0
    const offset = pendingLookup[shapeId] ?? { x: 0, y: 0 }
    return {
      ...shape,
      // Store effective center for sorting
      x: (shape.x ?? 0) + offset.x,
      y: (shape.y ?? 0) + offset.y,
      _groupId: group.id
    }
  }).filter(Boolean)

  // Sort using topological sort from helpers.ts
  // Note: We pass null for pendingUpdates since we've already applied offsets above
  const sortedShapes = sort3dShapeList(shapesForSort, null)

  // Map sorted shapes back to 3D groups
  const groupMap = new Map(shapeList3D.value.map(g => [g.id, g]))
  return sortedShapes.map(s => groupMap.get(s._groupId)).filter(Boolean)
})

// Debug labels for 3D z-order visualization
const debug3DLabels = computed(() => {
  if (!DEBUG_3D_SORT.value || !showDepthPreview.value || !shapeList3D.value) return []

  const pendingEntries = Array.from(pendingUpdates.value.entries())
  const pendingLookup = Object.fromEntries(pendingEntries)
  const sortedList = shapeList3DSorted.value
  const labels = []

  shapeList3D.value.forEach((group, idx) => {
    const shapeId = group.id.replace('-group', '')
    const shape = shapeList.value.find(s => s.id === shapeId)
    if (!shape) return

    // Get effective center position
    const offset = pendingLookup[shapeId] ?? { x: 0, y: 0 }
    const centerX = (shape.x ?? 0) + offset.x
    const centerY = (shape.y ?? 0) + offset.y
    const width = shape.width ?? 0
    const height = shape.height ?? 0
    const depth = shape.metadata?.depth ?? 0

    // Calculate Box3D bounds
    const backLeft = Math.round(centerX - width / 2)
    const backRight = Math.round(centerX + width / 2)
    const backTop = Math.round(centerY - height / 2)
    const backBottom = Math.round(centerY + height / 2)
    const frontRight = Math.round(backRight + depth)
    const frontBottom = Math.round(backBottom + depth)
    const isoDepth = backRight + backBottom

    // Find render order in sorted list
    const renderOrder = sortedList.findIndex(g => g.id === group.id)

    // Position labels at center of front face
    const frontCenterX = centerX + depth
    const frontCenterY = centerY + depth

    // Label dimensions for background
    const labelWidth = 130
    const labelHeight = 58
    const labelX = frontCenterX - labelWidth / 2
    const labelY = frontCenterY - labelHeight / 2

    // Background rectangle for readability
    labels.push({
      id: `debug-${shapeId}-bg`,
      _isRect: true,
      x: labelX - 4,
      y: labelY - 2,
      width: labelWidth + 8,
      height: labelHeight + 4,
      fill: 'rgba(0, 0, 0, 0.75)',
      cornerRadius: 4,
      listening: false
    })

    // Main debug label - render order and iso depth
    labels.push({
      id: `debug-${shapeId}-line1`,
      x: labelX,
      y: labelY,
      text: `#${renderOrder} iso:${isoDepth}`,
      fontSize: 14,
      fontFamily: 'monospace',
      fill: '#FFFF00',
      fontStyle: 'bold',
      listening: false
    })

    // Back face bounds
    labels.push({
      id: `debug-${shapeId}-line2`,
      x: labelX,
      y: labelY + 16,
      text: `B: L${backLeft} R${backRight}`,
      fontSize: 11,
      fontFamily: 'monospace',
      fill: '#88CCFF',
      listening: false
    })

    labels.push({
      id: `debug-${shapeId}-line3`,
      x: labelX,
      y: labelY + 28,
      text: `   T${backTop} B${backBottom}`,
      fontSize: 11,
      fontFamily: 'monospace',
      fill: '#88CCFF',
      listening: false
    })

    // Front extent
    labels.push({
      id: `debug-${shapeId}-line4`,
      x: labelX,
      y: labelY + 42,
      text: `F: R${frontRight} B${frontBottom}`,
      fontSize: 11,
      fontFamily: 'monospace',
      fill: '#88FF88',
      listening: false
    })
  })

  return labels
})

// Helper function to get label configs for a shape
const getLabelConfigs = (shape) => {
  const depth = shape.metadata?.depth || 0
  if (!depth) return []

  const colorLabel = getColorFromLabel(shape.metadata?.color_label) || '#FFFFFF'
  const color = getContrastingTextColor(colorLabel)
  const baseLabelHeight = 40
  const largeFontSize = 40
  const smallFontSize = 24
  const fontFamily = 'Montserrat'
  const isTurned = Math.abs((shape.rotation || 0) % 180) === 90
  const faceWidth = isTurned ? (shape.height || 0) : (shape.width || 0)

  // Use pending drag position if available, otherwise use store position
  const pendingPos = pendingUpdates.value.get(shape.id)
  const shapeX = pendingPos?.x ?? shape.x
  const shapeY = pendingPos?.y ?? shape.y

  const labelDepthValue = {
    id: `${shape.id}-label`,
    x: shapeX - faceWidth / 2,
    y: shapeY - baseLabelHeight / 2,
    width: faceWidth / 2,
    height: baseLabelHeight,
    fontSize: largeFontSize,
    fill: color,
    text: `${depth / INCH_IN_PIXELS}`,
    align: 'right',
    verticalAlign: 'bottom',
    fontFamily: fontFamily,
    listening: false,
    ignoreCollision: true,
  }

  const labelInch = {
    id: `${shape.id}-label-in`,
    x: shapeX,
    y: shapeY - baseLabelHeight / 2 - 3,
    width: faceWidth / 2,
    height: baseLabelHeight,
    fontSize: smallFontSize,
    fill: color,
    text: 'in',
    align: 'left',
    verticalAlign: 'bottom',
    fontFamily: fontFamily,
    listening: false,
    ignoreCollision: true,
  }

  return [labelDepthValue, labelInch]
}

// Helper function to get all available rotations for a shape
const getAvailableRotations = (shapeId) => {
  const shape = getShapeById(shapeId)
  if (!shape) return []

  // Find parent shape (one that has this shape as a child)
  const parentShape = getParentShape(shapeId)

  let rotations = []
  if (parentShape) {
    // This is a child, return parent + all children
    rotations = [{ id: parentShape.id, rotation: parentShape.rotation || 0 }]
    if (parentShape.children) {
      rotations.push(...parentShape.children.map(child => ({
        id: child.id,
        rotation: child.rotation || 0
      })))
    }
  } else if (shape.children && shape.children.length > 0) {
    rotations = [{ id: shape.id, rotation: shape.rotation || 0 }]
    rotations.push(...shape.children.map(child => ({
      id: child.id,
      rotation: child.rotation || 0
    })))
  }

  // On the 45° grid, 18mm layered shapes carry unrotated 0/90/180/270 rotations
  // from the square catalog, but must rotate with the grid's 45° baseline (45/135/225/315).
  if (gridPattern.value?.id === GRID_PATTERN_IDS.SQUARE_45 && isLayeredShape(shape)) {
    return rotations.map(r => ({
      ...r,
      rotation: ((r.rotation || 0) + 45) % 360,
    }))
  }

  return rotations
}

// Helper function to get the next rotation option
const getNextRotation = (currentShapeId) => {
  const rotations = getAvailableRotations(currentShapeId)
  if (rotations.length <= 1) return null

  const currentIndex = rotations.findIndex(r => r.id === currentShapeId)
  if (currentIndex === -1) return rotations[0]

  const nextIndex = (currentIndex + 1) % rotations.length
  return rotations[nextIndex]
}

// Hex lattices only map onto themselves under a 60 degree turn. Every other grid turns a quarter.
const blockTurnAngle = computed(() =>
  [GRID_PATTERN_IDS.HEX, GRID_PATTERN_IDS.HEX_90, GRID_PATTERN_IDS.HEX_SPACED].includes(gridPattern.value?.id) ? 60 : 90)

// Check if selected item has rotation options
const showRotateButton = computed(() => {
  // Every selected tile must support rotation.
  if (!selectedItemsOnGrid.value.length) return false

  // Hide rotate button while dragging
  if (isDragInProgress.value) return false

  // Only show for certain tools
  const allowedTools = [
    SELECTABLE_TOOLS.SELECT,
    SELECTABLE_TOOLS.ADD,
    SELECTABLE_TOOLS.LASSO,
    SELECTABLE_TOOLS.RECT_SELECT,
  ]
  if (!allowedTools.includes(selectedTool.value)) return false

  // Multi-selections turn as one rigid block on every grid, whatever their tiles can do alone.
  if (selectedItemsOnGrid.value.length > 1) return true
  return selectedItemsOnGrid.value.every(item => getAvailableRotations(item.metadata.shape_id).length > 1)
})

// Calculate rotate button position (bottom center of selected item's bounding box)
const rotateButtonConfig = computed(() => {
  if (!showRotateButton.value) return null
  if (selectedItemsOnGrid.value.length > 1) {
    const bounds = selectedItemsOnGrid.value.map(item => {
      const rad = (item.rotation || 0) * Math.PI / 180
      const w = item.width || getShapeById(item.metadata.shape_id)?.w || 120
      const h = item.height || getShapeById(item.metadata.shape_id)?.h || 120
      const halfW = (Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad))) / 2
      const halfH = (Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad))) / 2
      return { left: item.x - halfW, right: item.x + halfW, bottom: item.y + halfH, top: item.y - halfH }
    })
    return { id: 'selection-rotate-button', x: (Math.min(...bounds.map(b => b.left)) + Math.max(...bounds.map(b => b.right))) / 2,
      y: Math.min(...bounds.map(b => b.top)) - (isTouchDevice.value ? 40 : 28), listening: true }
  }

  const item = selectedItemsOnGrid.value[0]
  const shape = getShapeById(item.metadata.shape_id)
  if (!shape) return null

  // Get the shape dimensions
  const width = shape.w || item.width || 120
  const height = shape.h || item.height || 120
  const rotation = item.rotation || 0

  // Calculate all four corners of the rectangle relative to center
  const halfWidth = width / 2
  const halfHeight = height / 2
  const corners = [
    { x: -halfWidth, y: -halfHeight }, // top-left
    { x: halfWidth, y: -halfHeight },  // top-right
    { x: halfWidth, y: halfHeight },   // bottom-right
    { x: -halfWidth, y: halfHeight }   // bottom-left
  ]

  // Rotate all corners around the center (0, 0)
  const rotationRad = (rotation * Math.PI) / 180
  const cos = Math.cos(rotationRad)
  const sin = Math.sin(rotationRad)

  const rotatedCorners = corners.map(corner => ({
    x: corner.x * cos - corner.y * sin,
    y: corner.x * sin + corner.y * cos
  }))

  // Calculate bounding box
  const minX = Math.min(...rotatedCorners.map(c => c.x))
  const minY = Math.min(...rotatedCorners.map(c => c.y))
  const maxX = Math.max(...rotatedCorners.map(c => c.x))
  const maxY = Math.max(...rotatedCorners.map(c => c.y))

  // Position button at the bottom center of the bounding box, offset below with more spacing
  const centerX = (minX + maxX) / 2
  const offset = isTouchDevice.value ? 40 : 28
  const depthOffset = showDepthPreview.value ? (shape.d || item.metadata?.depth || 0) : 0
  const buttonX = item.x + centerX + depthOffset
  const buttonY = item.y + maxY + offset + depthOffset

  return {
    id: 'selection-rotate-button',
    x: buttonX,
    y: buttonY,
    listening: true,
  }
})

// Rotation icon using Konva Arrow (↺ anticlockwise reference)
const rotationIconConfig = computed(() => {
  // Create a curved anticlockwise arrow path forming ~270-degree arc
  // Starting from top-right (~1 o'clock), curving anticlockwise to bottom-left (~7 o'clock)
  // Using points that form a circular arc matching ↺ character
  let points = []
  let pointerLength = 8
  let pointerWidth = 8
  let strokeWidth = 2

  if (isTouchDevice.value) {
    const radius = 10
    // Generate points along a ~270-degree arc anticlockwise
    const startAngle = -30  // Start at top-right (~1 o'clock)
    const endAngle = 240    // End at bottom-left (~7 o'clock)
    const steps = 14
    for (let i = 0; i <= steps; i++) {
      const angle = (startAngle + (endAngle - startAngle) * (i / steps)) * (Math.PI / 180)
      points.push(radius * Math.cos(angle))
      points.push(radius * Math.sin(angle))
    }
    pointerLength = 6
    pointerWidth = 6
    strokeWidth = 2.5
  } else {
    const radius = 6.5
    // Generate points along a ~270-degree arc anticlockwise
    const startAngle = -30  // Start at top-right (~1 o'clock)
    const endAngle = 240    // End at bottom-left (~7 o'clock)
    const steps = 12
    for (let i = 0; i <= steps; i++) {
      const angle = (startAngle + (endAngle - startAngle) * (i / steps)) * (Math.PI / 180)
      points.push(radius * Math.cos(angle))
      points.push(radius * Math.sin(angle))
    }
    pointerLength = 4
    pointerWidth = 4
    strokeWidth = 2
  }

  return {
    points,
    tension: 0.5,
    pointerLength,
    pointerWidth,
    pointerAtBeginning: false,
    pointerAtEnding: true,
    fill: '#313558',
    stroke: '#313558',
    strokeWidth,
    lineCap: 'round',
    lineJoin: 'round',
  }
})

const tempItemAttrs = { opacity: 0.75, fill: null, collision: false };
const tempResetedItemAttrs = { opacity: 1, fill: null, collision: false };
const tempCollidedItemAttrs = { opacity: 0.5, fill: "red", collision: true };

// todo: workaround. needs to improve that
const valid45degPositions = ref([])
const valid45degBitPositions = ref([])
const validSpacedHexPositions = ref([])
const validSpacedDiamondPositions = ref([])

// about mouse events
const lastCenterTouch = ref(null)
const lastDistTouch = ref(0)
const dragStopped = ref(false)
const isTouching = ref(false)

// Mobile resize handle tracking
const mobileResizeStartTime = ref(0)
const mobileResizeStartPos = ref({ x: 0, y: 0 })
const isMobileHandleTouched = ref(false)

// Tap vs swipe detection
const touchStartPosition = ref({ x: 0, y: 0 })
const isTouchMoved = ref(false)
const touchStartedOnStage = ref(false) // Track if touch started on empty stage area
const wasMultiTouch = ref(false) // Track if gesture involved multiple touches
const multiTouchEndTime = ref(0) // Track when multi-touch ended
const SWIPE_THRESHOLD = 10 // pixels - adjust as needed
const MULTI_TOUCH_COOLDOWN = 3000 // ms - prevent tile placement after multi-touch (increased for sequential finger lifts)

const initialPositions = ref(new Map());
const dragThrottleId = ref(null);
const pendingUpdates = ref(new Map());
const isDragInProgress = ref(false);

watch(gridPos, () => {
  stageRef.value.getStage().position(gridPos.value);
})

watch(gridZoom, () => {
  const stage = stageRef.value.getStage();
  stage.scale({ x: gridZoom.value, y: gridZoom.value });
  stage.batchDraw();
})

watch(showDepthPreview, () => {
  store.updateShapeList3D();
})

// Watch for tool and selection changes
watch([selectedTool, selectedShape, selectedColor, selectedRotation], ([newTool, newShape, newColor]) => {
  if (newShape && newColor) {
    // Set temp item immediately when selection changes
    store.setTempItemByCurrentSelection()
  }
}, { immediate: true })

watch([gridWidth, gridHeight, gridPattern, placementSnap], () => {
  if (gridPattern.value.id === GRID_PATTERN_IDS.SQUARE_45) {
    valid45degPositions.value = calculateValid45degPosition()
    valid45degBitPositions.value = calculateValid45degBitPosition()

    // Build optimized data structures for fast snapping
    optimized45degSnapping.buildOptimizedStructures()

    // Debug: Log the number of valid positions to ensure consistency
    if (import.meta.dev) {
      console.log(`[45deg Grid] Recalculated valid positions:`, {
        mainPositions: valid45degPositions.value.length,
        bitPositions: valid45degBitPositions.value.length,
        gridWidth: gridWidth.value,
        gridHeight: gridHeight.value,
        snapX: gridPattern.value.snap.x,
        snapY: gridPattern.value.snap.y
      })
    }
  }
  if (gridPattern.value.id === 'hex-spaced') {
    validSpacedHexPositions.value = calculateValidSpacedHexPosition()
    validSpacedDiamondPositions.value = calculateValidSpacedDiamondPosition()
  }
})

watch(somethingChanged, (cur, prev) => {
  keepSelection.value = cur <= prev
})

// Watch shapeList to detect additions/removals and property changes
// Using a getter function ensures reactivity when the array is reassigned
watch([() => shapeList.value, showDepthPreview], () => {
  if (gridPattern.value.collisionLevel === CollisionLevel.NONE) return

  nextTick(() => {
    updateDrawnShapesForCollision()
  })
}, { deep: true, immediate: true })

const baseLassoConfig = {
  type: "line",
  stroke: "red",
  strokeWidth: 2,
  dash: [8, 8],
  dashOffset: 4,
  lineCap: "round",
  lineJoin: "round",
  closed: false,
};

const baseRectConfig = {
  type: "rect",
  stroke: "red",
  strokeWidth: 2,
  dash: [8, 8],
  dashOffset: 4,
  fill: "rgba(255, 0, 0, 0.1)",
};

// Grid resize indicator configurations
const rightEdgeIndicator = computed(() => {
  // Position indicators at the actual grid edges in grid coordinates
  const rightEdge = startOffset.value + gridWidth.value;
  const topEdge = startOffset.value;
  const bottomEdge = startOffset.value + gridHeight.value;

  return {
    points: [
      rightEdge, topEdge,
      rightEdge, bottomEdge
    ],
    stroke: '#007bff',
    strokeWidth: 3,
    opacity: 0.8,
    listening: false,
  };
});

const bottomEdgeIndicator = computed(() => {
  // Position indicators at the actual grid edges in grid coordinates
  const leftEdge = startOffset.value;
  const rightEdge = startOffset.value + gridWidth.value;
  const bottomEdge = startOffset.value + gridHeight.value;

  return {
    points: [
      leftEdge, bottomEdge,
      rightEdge, bottomEdge
    ],
    stroke: '#007bff',
    strokeWidth: 3,
    opacity: 0.8,
    listening: false,
  };
});

// Mobile resize handle configurations
const rightEdgeMobileHandle = computed(() => {
  const targetSize = touchTargetSize.value;
  const rightEdge = startOffset.value + gridWidth.value;
  const centerY = startOffset.value + gridHeight.value / 2;

  return {
    x: rightEdge - targetSize / 4, // Slightly overlap with grid edge
    y: centerY - targetSize / 2,
    width: targetSize / 2,
    height: targetSize,
    fill: '#007bff',
    opacity: 0.7,
    cornerRadius: 4,
    stroke: '#0056b3',
    strokeWidth: 1,
    shadowColor: 'rgba(0, 0, 0, 0.3)',
    shadowBlur: 2,
    shadowOffset: { x: 1, y: 1 },
    listening: true,
  };
});

const bottomEdgeMobileHandle = computed(() => {
  const targetSize = touchTargetSize.value;
  const bottomEdge = startOffset.value + gridHeight.value;
  const centerX = startOffset.value + gridWidth.value / 2;

  return {
    x: centerX - targetSize / 2,
    y: bottomEdge - targetSize / 4, // Slightly overlap with grid edge
    width: targetSize,
    height: targetSize / 2,
    fill: '#007bff',
    opacity: 0.7,
    cornerRadius: 4,
    stroke: '#0056b3',
    strokeWidth: 1,
    shadowColor: 'rgba(0, 0, 0, 0.3)',
    shadowBlur: 2,
    shadowOffset: { x: 1, y: 1 },
    listening: true,
  };
});

const cornerMobileHandle = computed(() => {
  const targetSize = touchTargetSize.value;
  const rightEdge = startOffset.value + gridWidth.value;
  const bottomEdge = startOffset.value + gridHeight.value;

  return {
    x: rightEdge - targetSize / 3,
    y: bottomEdge - targetSize / 3,
    width: targetSize * 0.7,
    height: targetSize * 0.7,
    fill: '#007bff',
    opacity: 0.8,
    cornerRadius: 6,
    stroke: '#0056b3',
    strokeWidth: 2,
    shadowColor: 'rgba(0, 0, 0, 0.4)',
    shadowBlur: 3,
    shadowOffset: { x: 2, y: 2 },
    listening: true,
  };
});

// HTML Mobile resize handle styles - positioned absolutely over the canvas
const rightEdgeHandleStyle = computed(() => {
  const targetSize = touchTargetSize.value;
  const stage = stageRef.value?.getStage();
  if (!stage) return { display: 'none' };

  // Use reactive values that Vue can track
  const stagePos = gridPos.value;
  const zoom = gridZoom.value;

  // Calculate position in screen coordinates
  const rightEdge = (startOffset.value + gridWidth.value) * zoom + stagePos.x;
  const centerY = (startOffset.value + gridHeight.value / 2) * zoom + stagePos.y;

  return {
    position: 'absolute',
    left: `${rightEdge - targetSize / 4}px`,
    top: `${centerY - targetSize / 2}px`,
    width: `${targetSize / 2}px`,
    height: `${targetSize}px`,
    backgroundColor: '#007bff',
    opacity: '0.7',
    borderRadius: '4px',
    border: '1px solid #0056b3',
    boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
    touchAction: 'none',
    userSelect: 'none',
    zIndex: '20',
  };
});

const bottomEdgeHandleStyle = computed(() => {
  const targetSize = touchTargetSize.value;
  const stage = stageRef.value?.getStage();
  if (!stage) return { display: 'none' };

  // Use reactive values that Vue can track
  const stagePos = gridPos.value;
  const zoom = gridZoom.value;

  // Calculate position in screen coordinates
  const centerX = (startOffset.value + gridWidth.value / 2) * zoom + stagePos.x;
  const bottomEdge = (startOffset.value + gridHeight.value) * zoom + stagePos.y;

  return {
    position: 'absolute',
    left: `${centerX - targetSize / 2}px`,
    top: `${bottomEdge - targetSize / 4}px`,
    width: `${targetSize}px`,
    height: `${targetSize / 2}px`,
    backgroundColor: '#007bff',
    opacity: '0.7',
    borderRadius: '4px',
    border: '1px solid #0056b3',
    boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
    touchAction: 'none',
    userSelect: 'none',
    zIndex: '20',
  };
});

const cornerHandleStyle = computed(() => {
  const targetSize = touchTargetSize.value;
  const stage = stageRef.value?.getStage();
  if (!stage) return { display: 'none' };

  // Use reactive values that Vue can track
  const stagePos = gridPos.value;
  const zoom = gridZoom.value;

  // Calculate position in screen coordinates
  const rightEdge = (startOffset.value + gridWidth.value) * zoom + stagePos.x;
  const bottomEdge = (startOffset.value + gridHeight.value) * zoom + stagePos.y;

  return {
    position: 'absolute',
    left: `${rightEdge - targetSize / 3}px`,
    top: `${bottomEdge - targetSize / 3}px`,
    width: `${targetSize * 0.7}px`,
    height: `${targetSize * 0.7}px`,
    backgroundColor: '#007bff',
    opacity: '0.8',
    borderRadius: '6px',
    border: '2px solid #0056b3',
    boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
    touchAction: 'none',
    userSelect: 'none',
    zIndex: '20',
  };
});

const resetLasso = () => {
  pathPoints.value = [];
  lastPoint.value = null;
  isDrawing.value = false;
  store.setLassoSelectionItem(null);
  // Cancel any pending lasso updates
  if (lassoThrottleId.value) {
    cancelAnimationFrame(lassoThrottleId.value);
    lassoThrottleId.value = null;
  }
};

const resetRect = () => {
  lastPoint.value = null;
  isDrawing.value = false;
  store.setRectSelectionItem(null);
};

// Watch for tool switching to clear selection items
watch(selectedTool, (newTool, oldTool) => {
  // Clear selection items when switching tools
  if (oldTool === SELECTABLE_TOOLS.LASSO) {
    resetLasso();
  }
  if (oldTool === SELECTABLE_TOOLS.RECT_SELECT) {
    resetRect();
  }

  // Clear selection items when switching to non-selection tools
  if (![SELECTABLE_TOOLS.LASSO, SELECTABLE_TOOLS.RECT_SELECT].includes(newTool)) {
    resetLasso();
    resetRect();
  }

  // Clear auto-expand candidate ghosts when leaving placement mode.
  if (newTool !== SELECTABLE_TOOLS.ADD) {
    if (candidateTimer) {
      clearTimeout(candidateTimer)
      candidateTimer = null
    }
    dropCandidates.value = []
  }
});

// Helper function to get bounding box of lasso path for spatial culling
const getLassoBoundingBox = (points) => {
  if (points.length < 4) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  return { minX, minY, maxX, maxY };
};

// Helper to get shape bounding box for 3D label visibility check
const getShapeBoundingBox = (shape) => {
  const pendingPos = pendingUpdates.value.get(shape.id)
  const x = pendingPos?.x ?? shape.x
  const y = pendingPos?.y ?? shape.y
  const halfWidth = (shape.width || 0) / 2
  const halfHeight = (shape.height || 0) / 2

  return {
    minX: x - halfWidth,
    minY: y - halfHeight,
    maxX: x + halfWidth,
    maxY: y + halfHeight
  }
}

// Check if a point is inside a bounding box
const isPointInBoundingBox = (point, box) => {
  return point.x >= box.minX && point.x <= box.maxX &&
         point.y >= box.minY && point.y <= box.maxY
}

// Computed property for shapes with visible labels (not covered by shapes on top)
const shapesWithVisibleLabels = computed(() => {
  if (gridPattern.value?.id !== GRID_PATTERN_IDS.THREE_D || showDepthPreview.value) {
    return []
  }

  const list = shapeList.value
  const result = []

  for (let i = 0; i < list.length; i++) {
    const shape = list[i]
    const depth = shape.metadata?.depth || 0
    if (!depth) continue // Skip shapes without depth (no label to show)

    // Get the label center position (where the inch text is displayed)
    const pendingPos = pendingUpdates.value.get(shape.id)
    const labelCenter = {
      x: pendingPos?.x ?? shape.x,
      y: pendingPos?.y ?? shape.y
    }

    // Check if any shape that comes AFTER this one (i.e., on top) covers the label
    let isCovered = false
    for (let j = i + 1; j < list.length; j++) {
      const topShape = list[j]
      const topBox = getShapeBoundingBox(topShape)

      if (isPointInBoundingBox(labelCenter, topBox)) {
        isCovered = true
        break
      }
    }

    if (!isCovered) {
      result.push(shape)
    }
  }

  return result
})

const checkAllTempCollision = () => {
  const group = tempGroupRef.value?.getNode();
  const shapes = group?.children.filter((node) => !(node instanceof Konva.Group));
  const shapesInGroups = group?.children.filter((node) => (node instanceof Konva.Group))

  if (!group || !shapes || (shapes.length + shapesInGroups.length) < 1) {
    return;
  }

  shapes.forEach((shapeOnGrid) => checkTempItemCollision(shapeOnGrid));
  shapesInGroups.forEach((groupOnGrid) => {
    const frontFace = groupOnGrid.children.find((child) => child.attrs.ignoreCollision !== true);
    if (frontFace) {
      checkTempItemCollision(frontFace, groupOnGrid)
    }
  });
};

const checkTempItemCollision = (tempItemOnGrid, group = []) => {
  if (!tempItemOnGrid) return

  function applyAttrs(attrs) {
    tempItemOnGrid.setAttrs(attrs)

    if (group.children) {
      const id = tempItemOnGrid.id()
      group.children
        .filter(face => [`${id}-top`, `${id}-left`].includes(face.id()))
        .forEach(face => face.setAttrs(attrs))
    }
  }

  applyAttrs(tempItemAttrs)

  const shape1Points = getShapePoints(tempItemOnGrid, gridZoom.value)

  drawnShapesForCollision.value.forEach((shape) => {
    if (shape === tempItemOnGrid) return false;

    const shape2Points = getShapePoints(shape, gridZoom.value)
    const intersects = checkShapeIntersection(shape1Points, shape2Points);

    if (intersects) {
      applyAttrs(tempCollidedItemAttrs)
    }
  })
}

// Offset ShapePoints by a given amount (used to subtract depth for 2D collision)
const offsetShapePoints = (shapePoints, dx, dy) => ({
  type: shapePoints.type,
  points: shapePoints.points.map(p => ({ x: p.x - dx, y: p.y - dy }))
})

const checkDragCollision = () => {
  if (!shapesLayerRef.value) return

  const shapesLayer = shapesLayerRef.value.getNode()
  if (!shapesLayer) return

  const draggedIds = new Set(selectedItemsOnGrid.value.map(item => item.id))
  let hasCollision = false
  const isDepth = showDepthPreview.value
  const zoom = gridZoom.value

  // Collect all dragged Konva nodes (front faces for 3D groups, shapes for flat)
  const draggedNodes = []
  selectedItemsOnGrid.value.forEach(item => {
    if (isDepth) {
      const group = shapesLayer.findOne(`#${item.id}-group`)
      if (group && group instanceof Konva.Group) {
        const frontFace = group.children.find(child => child.id() === item.id)
        if (frontFace) draggedNodes.push({ node: frontFace, group })
      }
    } else {
      const node = shapesLayer.findOne(`#${item.id}`)
      if (node) draggedNodes.push({ node, group: null })
    }
  })

  // Check each dragged node against collision shapes, excluding dragged shapes themselves
  // Use layer as relativeTo so all points are in the same coordinate space
  // In depth mode, points are front-face in layer space; subtract depth (layer units) to get footprint
  for (const { node } of draggedNodes) {
    let shape1Points = getShapePoints(node, zoom, shapesLayer)
    if (isDepth) {
      const depth1 = node.attrs.metadata?.depth || 0
      shape1Points = offsetShapePoints(shape1Points, depth1, depth1)
    }

    for (const collisionShape of drawnShapesForCollision.value) {
      const collisionId = collisionShape.id()
      if (draggedIds.has(collisionId)) continue

      let shape2Points = getShapePoints(collisionShape, zoom, shapesLayer)
      if (isDepth) {
        const depth2 = collisionShape.attrs.metadata?.depth || 0
        shape2Points = offsetShapePoints(shape2Points, depth2, depth2)
      }

      if (checkShapeIntersection(shape1Points, shape2Points)) {
        hasCollision = true
        break
      }
    }
    if (hasCollision) break
  }

  dragCollision.value = hasCollision

  // Save original opacities on first pass (before any collision feedback modifies them)
  draggedNodes.forEach(({ group }) => {
    if (group) {
      group.children.forEach(face => {
        if (face._originalOpacity == null) face._originalOpacity = face.opacity()
      })
    }
  })

  // Apply visual feedback to all dragged nodes
  draggedNodes.forEach(({ node, group }) => {
    if (hasCollision) {
      if (group) {
        group.children.forEach(face => face.opacity(0.5))
        node.fill('red')
      } else {
        node.opacity(0.5)
        node.fill('red')
      }
    } else {
      if (group) {
        // Restore each face's original opacity (preserves black overlay faces)
        group.children.forEach(face => {
          face.opacity(face._originalOpacity ?? 1)
        })
        node.fill(node.attrs.metadata?.color || null)
      } else {
        node.opacity(1)
        node.fill(node.attrs.metadata?.color || null)
      }
    }
  })
}

const applyShapePositionLimit = (shape, position) => {
  const shapeID = shape.id
  let width = shape.w
  let height = shape.h

  if (gridPattern.value.id === 'hex-spaced') {
    position.x = Math.max(Math.min(position.x, gridWidth.value),  -width/2)
    position.y = Math.max(Math.min(position.y, gridHeight.value), -height/2)
    return
  }

  // need to invert it since its rotation
  if (['ShiplapVertical', 'MiniShiplapVertical'].includes(shapeID)) {
    let temp = height
    height = width
    width = temp
  }

  if (gridPattern.value.id === GRID_PATTERN_IDS.SQUARE_45) {
    width *= Math.sqrt(2)
    height *= Math.sqrt(2)
  }

  const offset = { x: width / 2, y: height / 2 }

  // todo: improve hex/45deg shapes positions
  const snapMultipliers = {
    x: {
      basic: ['Pixel', 'MiniShiplapVertical', 'HalfCircleVerticalFlipped'],
      bit: ['Bit', 'Bit45deg'],
      full: ['LargeHalfCircleVertical', 'QuarterCircleBottomLeft', 'QuarterCircleTopLeft'],
      zero: ['LargeHalfCircleVerticalFlipped', 'QuarterCircleBottomRight', 'QuarterCircleTopRight'],
      extended: ['HalfCircleVertical']
    },
    y: {
      basic: ['Pixel', 'Bit', 'Bit45deg', 'MiniShiplapHorizontal', 'HalfCircleHorizontalFlipped'],
      full: ['LargeHalfCircleHorizontal', 'QuarterCircleTopLeft', 'QuarterCircleTopRight'],
      zero: ['LargeHalfCircleHorizontalFlipped', 'QuarterCircleBottomLeft', 'QuarterCircleBottomRight'],
      extended: ['HalfCircleHorizontal']
    }
  }

  const getMin = (axis) => {
    const rules = snapMultipliers[axis]

    if (rules.basic.includes(shapeID)) {
      return -offset[axis] + gridPattern.value.snap[axis]
    }
    if (rules.bit?.includes(shapeID)) {
      return -offset[axis] + gridPattern.value.snap[axis] * 1.5
    }
    if (rules.full?.includes(shapeID)) {
      const size =  axis === 'x' ? width : height
      return -offset[axis] + size
    }
    if (rules.zero?.includes(shapeID)) {
      return -offset[axis]
    }
    if (rules.extended?.includes(shapeID)) {
      const size =  axis === 'x' ? width : height
      return -offset[axis] + size + gridPattern.value.snap[axis]
    }

    return -offset[axis] + gridPattern.value.snap[axis] * 2
  }

  const getMax = (axis) => {
    const rules = snapMultipliers[axis]
    if (rules.basic.includes(shapeID)) {
      return axis === 'x' ? gridWidth.value : gridHeight.value
    }
    if (rules.zero.includes(shapeID)) {
      return (axis === 'x') ? gridWidth.value : gridHeight.value
    }
    if (rules.full?.includes(shapeID)) {
      const grid = axis === 'x' ? gridWidth.value : gridHeight.value
      const size =  axis === 'x' ? width : height
      return grid + size
    }
    if (rules.extended?.includes(shapeID) && axis === 'x') {
      return gridWidth.value + width
    }

    return (axis === 'x' ? gridWidth.value : gridHeight.value) + offset[axis]
  }

  position.x = Math.max(Math.min(position.x, getMax('x')), getMin('x'))
  position.y = Math.max(Math.min(position.y, getMax('y')), getMin('y'))
}

const adjustOffsetByShape = (shape) => {
  const offset = { x: shape.w / 2, y: shape.h / 2 }

  switch (shape.type) {
    case 'rect':
      offset.x = 0
      offset.y = 0

      if (shape.id === 'Pixel') {
        offset.x = 10
        offset.y = 10
      }
      if (shape.id === 'Bit') {
        offset.x = -5
        offset.y = 10
      }
      break
    case 'rect-triangle':
      offset.x = shape.w / 3
      offset.y = -shape.h / 3
      break
    case 'arc':
      if (shape.angle === 180) {
        offset.x = 0
        offset.y = shape.h / 4
      } else {
        offset.x = shape.w / 2
        offset.y = shape.h / 2
      }
      break
    case 'hex':
    case 'diamond':
    case 'triangle':
      offset.x *= -0.1
      offset.y *= -0.1
      break
  }

  return offset
}

const calcMoveItemPosition = (shapeOrItem) => {
  // If shapeOrItem is a ShapeInterface (has metadata.shape_id), extract the shape
  let shape = shapeOrItem
  if (shapeOrItem?.metadata?.shape_id) {
    // It's a ShapeInterface item - get the shape definition and merge with item properties
    const shapeDef = getShapeById(shapeOrItem.metadata.shape_id)
    if (shapeDef) {
      // Merge shape definition with item's actual properties (rotation, width, height, etc.)
      shape = {
        ...shapeDef,
        rotation: shapeOrItem.rotation !== undefined ? shapeOrItem.rotation : (shapeDef.rotation || 0),
        // Use item's width/height if available, otherwise use shape definition's w/h
        w: shapeOrItem.width !== undefined ? shapeOrItem.width : shapeDef.w,
        h: shapeOrItem.height !== undefined ? shapeOrItem.height : shapeDef.h,
        // Preserve other shape properties that might be needed
        d: shapeOrItem.metadata?.depth !== undefined ? shapeOrItem.metadata.depth : shapeDef.d,
      }
    } else {
      // Fallback: if shape definition not found, try to construct shape from item properties
      // This handles edge cases where shape_id might be invalid
      shape = {
        ...shapeOrItem,
        w: shapeOrItem.width || shapeOrItem.w || 0,
        h: shapeOrItem.height || shapeOrItem.h || 0,
        rotation: shapeOrItem.rotation || 0,
      }
    }
  }

  const stage = stageRef.value.getStage()
  const stagePos = stage.position()
  const pointerPos = stage.getPointerPosition()
  const layerX = Math.round((pointerPos.x - stagePos.x) / gridZoom.value)
  const layerY = Math.round((pointerPos.y - stagePos.y) / gridZoom.value)
  const offset = adjustOffsetByShape(shape)
  const position = getRotatedPosition(layerX, layerY, offset.x, offset.y, shape.rotation)
  applyShapePositionLimit(shape, position)
  const snapped = getSnapGridPosition(position.x, position.y, shape)

  // adjust position for 3D item moving
  if (showDepthPreview.value && shape.d) {
    snapped.x += shape.d / 2
    snapped.y += shape.d / 2
  }

  return snapped
}

// A slat turned by block rotation covers its width and height swapped.
const slatFootprint = (item) => {
  const w = item.width ?? getShapeById(item.metadata.shape_id).w
  const h = item.height ?? getShapeById(item.metadata.shape_id).h
  return Math.abs((item.rotation || 0) % 180) === 90 ? { w: h, h: w } : { w, h }
}

// Snapping needs the footprint a tile covers now. Slats keep their saved size, which can differ from the catalog
// default, and a turned slat swaps its width and height.
const snapShapeOf = (item) => {
  const shape = getShapeById(item.metadata.shape_id)
  return shape && isWoodSlatShapeId(shape.id) ? { ...shape, ...slatFootprint(item) } : shape
}

const getShapeToSnap = (items, bounds) => {
  if(items.value.length === 1) return snapShapeOf(items.value[0])

  let shape = undefined
  const gridModifier = gridPattern.value.id === GRID_PATTERN_IDS.SQUARE_45 ? '45deg' : ''
  const modX = (bounds.maxX - bounds.minX) % gridPattern.value.padding.x
  const modY = (bounds.maxY - bounds.minY) % gridPattern.value.padding.y

  if (gridPattern.value.id === GRID_PATTERN_IDS.SQUARE_45) {
    const isModXEqualModY = Math.round(modX) === Math.round(modY)
    const isModXEqualPadX = Math.round(modX) === Math.round(gridPattern.value.padding.x)
    const isModXGreaterThanSnapX = modX > gridPattern.value.snap.x
    const isModYGreaterThanSnapY = modY > gridPattern.value.snap.y

    shape = getShapeById('Quarter'+gridModifier)

    if (isModXGreaterThanSnapX && isModYGreaterThanSnapY && isModXEqualModY && !isModXEqualPadX) {
      shape = getShapeById('MiniShiplapVertical'+gridModifier)
    }
    else if (isModYGreaterThanSnapY && isModXEqualModY && !isModXEqualPadX) {
      shape = getShapeById('MiniShiplapHorizontal'+gridModifier)
    }
  }
  else {
    if (modX > 0 && modY > 0) {
      shape = getShapeById('Pixel'+gridModifier)
    }
    else if (modX > 0) {
      shape = getShapeById('MiniShiplapVertical'+gridModifier)
    }
    else if (modY > 0) {
      shape = getShapeById('MiniShiplapHorizontal'+gridModifier)
    }
  }

  return shape
}

const calcItemsPositionBasedOnPointer = (items) => {
  // Get the current mouse position relative to the stage
  const stage = stageRef.value.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();
  const layerX = Math.round((pointerPos.x - stagePos.x) / gridZoom.value);
  const layerY = Math.round((pointerPos.y - stagePos.y) / gridZoom.value);

  // Initialize or update initial positions if not set
  if (!initialPositions.value.size) {
    items.value.forEach(item => {
      initialPositions.value.set(item.id, {
        x: item.x,
        y: item.y,
        rotation: item.rotation || 0
      });
    });
  }

  // Optimized bounds calculation - only calculate once
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const itemShapes = new Map(); // Cache shape lookups

  items.value.forEach(item => {
    const initialPos = initialPositions.value.get(item.id);
    if (!initialPos) return;

    // Cache shape lookup to avoid repeated getShapeById calls
    if (!itemShapes.has(item.metadata.shape_id)) {
      itemShapes.set(item.metadata.shape_id, getShapeById(item.metadata.shape_id));
    }
    const shape = itemShapes.get(item.metadata.shape_id);

    const width = shape.w;
    const height = shape.h;

    // Calculate the effective dimensions based on rotation
    let effectiveWidth, effectiveHeight;
    if (initialPos.rotation === 45 || initialPos.rotation === 225) {
      // For 45-degree rotations, use the diagonal length
      const diagonal = Math.sqrt(width * width + height * height);
      effectiveWidth = effectiveHeight = diagonal;
    } else if (initialPos.rotation === 90 || initialPos.rotation === 270) {
      // For 90-degree rotations, swap width and height
      effectiveWidth = height;
      effectiveHeight = width;
    } else {
      // For other rotations, use original dimensions
      effectiveWidth = width;
      effectiveHeight = height;
    }

    minX = Math.min(minX, initialPos.x - effectiveWidth/2);
    minY = Math.min(minY, initialPos.y - effectiveHeight/2);
    maxX = Math.max(maxX, initialPos.x + effectiveWidth/2);
    maxY = Math.max(maxY, initialPos.y + effectiveHeight/2);
  });

  const initialCenterX = (minX + maxX) / 2;
  const initialCenterY = (minY + maxY) / 2;

  // Calculate the offset from the initial center to the current pointer
  const offsetX = layerX - initialCenterX;
  const offsetY = layerY - initialCenterY;

  // Use optimized snapping for the center point
  const snappedCenter = getSnapGridPosition(
    initialCenterX + offsetX,
    initialCenterY + offsetY,
    getShapeToSnap(items, { minX, minY, maxX, maxY }),
  );

  // Calculate the actual offset to apply (difference between snapped and initial center)
  const finalOffsetX = snappedCenter.x - initialCenterX;
  const finalOffsetY = snappedCenter.y - initialCenterY;

  // Move each item maintaining their relative positions from initial state
  // Use group snapping for performance during drag operations
  const newItems = []
  items.value.forEach(item => {
    const initialPos = initialPositions.value.get(item.id);
    if (!initialPos) return;

    // Calculate new position based on initial position plus the snapped offset
    const newX = initialPos.x + finalOffsetX;
    const newY = initialPos.y + finalOffsetY;

    newItems.push({
      ...item,
      ...item.attrs,
      x: newX,
      y: newY,
      rotation: initialPos.rotation,
      stroke: null,
      strokeWidth: 0,
      ...tempItemAttrs,
    })
  });

  return newItems;
};

const calcTempItemsPositionBasedOnPointer = (items) => {
  // Get the current mouse position relative to the stage
  const stage = stageRef.value.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();
  const layerX = Math.round((pointerPos.x - stagePos.x) / gridZoom.value);
  const layerY = Math.round((pointerPos.y - stagePos.y) / gridZoom.value);

  // Initialize or update initial positions if not set
  if (!initialPositions.value.size) {
    items.value.forEach(item => {
      initialPositions.value.set(item.id, {
        x: item.x,
        y: item.y,
        rotation: item.rotation || 0
      });
    });
  }

  // Calculate the bounds and center of initial positions
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  items.value.forEach(item => {
    const initialPos = initialPositions.value.get(item.id);
    if (!initialPos) return;

    minX = Math.min(minX, initialPos.x);
    minY = Math.min(minY, initialPos.y);
    maxX = Math.max(maxX, initialPos.x);
    maxY = Math.max(maxY, initialPos.y);
  });

  const initialCenterX = (minX + maxX) / 2;
  const initialCenterY = (minY + maxY) / 2;

  // Calculate the offset from the initial center to the current pointer
  const offsetX = layerX - initialCenterX;
  const offsetY = layerY - initialCenterY;


  // Step 1: Use first item as the group leader for snapping
  const leaderItem = items.value[0];
  if (!leaderItem) return [];

  const leaderInitialPos = initialPositions.value.get(leaderItem.id);
  if (!leaderInitialPos) return [];

  // Step 2: Calculate leader's target position and apply snapping
  const rawLeaderX = leaderInitialPos.x + offsetX;
  const rawLeaderY = leaderInitialPos.y + offsetY;

  const leaderShape = getShapeById(leaderItem.metadata.shape_id);
  let snappedLeaderX = rawLeaderX;
  let snappedLeaderY = rawLeaderY;

  if (leaderShape) {
    const snapped = getSnapGridPosition(rawLeaderX, rawLeaderY, leaderShape);
    snappedLeaderX = snapped.x;
    snappedLeaderY = snapped.y;
  }

  // Step 3: Calculate the unified offset for the entire group
  const groupOffsetX = snappedLeaderX - leaderInitialPos.x;
  const groupOffsetY = snappedLeaderY - leaderInitialPos.y;


  // Step 4: Apply the same offset to ALL temp items (maintaining relative positions)
  const newItems = []
  items.value.forEach(item => {
    const initialPos = initialPositions.value.get(item.id);
    if (!initialPos) return;

    // Apply the unified group offset to maintain relative positions
    const finalX = initialPos.x + groupOffsetX;
    const finalY = initialPos.y + groupOffsetY;


    newItems.push({
      ...item,
      ...item.attrs,
      x: finalX,
      y: finalY,
      rotation: initialPos.rotation,
      stroke: null,
      strokeWidth: 0,
      ...tempItemAttrs,
    })
  });

  return newItems;
};

// Reset initial positions when selection changes (but not during active drag)
watch(selectedItemsOnGrid, () => {
  // Don't clear initial positions during an active drag operation
  if (!isDragInProgress.value) {
    initialPositions.value.clear();
    // Cancel any pending throttled updates
    if (dragThrottleId.value) {
      cancelAnimationFrame(dragThrottleId.value);
      dragThrottleId.value = null;
    }
    pendingUpdates.value.clear();
  }
}, { deep: true });

// Reset initial positions when temp items change (e.g., quantity or shape/color changes)
watch(tempNewItems, (newItems, oldItems) => {
  // Clear initial positions when temp items change (but not during active drag)
  if (!isDragInProgress.value) {
    // Check if items have actually changed (different length or different IDs)
    const hasChanged = !oldItems ||
      newItems.length !== oldItems.length ||
      newItems.some((item, index) => !oldItems[index] || item.id !== oldItems[index].id);

    if (hasChanged) {
      initialPositions.value.clear();
    }
  }
}, { deep: false });

// todo: move grid calculations to each grid pattern instance, consider apply OOP
const calculateValid45degPosition = () => {
  const snapX = placementSnap.value.x * 2
  const snapY = placementSnap.value.y * 2
  const cols = Math.ceil(gridWidth.value / snapX)
  const rows = Math.ceil(gridHeight.value / snapY)
  const validPos1 = { x: [], y: [] }
  const validPos2 = { x: [], y: [] }
  const validPixel1 = { x: [], y: [] }
  const validPixel2 = { x: [], y: [] }
  const validSmall1 = { x: [], y: [] }
  const validSmall2 = { x: [], y: [] }
  const validSmall3 = { x: [], y: [] }
  const validSmall4 = { x: [], y: [] }

  // Helper function to round to avoid floating-point precision issues
  const roundToPrecision = (value, precision = 2) => {
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision)
  }

  for (let i = 0; i <= (cols*2); i++) {
    const pos = roundToPrecision(i * snapX)
    const two = roundToPrecision(pos + (snapX/2))
    validPos1.x.push(pos)
    validPos2.x.push(two)
    validSmall1.x.push(roundToPrecision(pos + (snapX/4)))
    validSmall2.x.push(roundToPrecision(two + (snapX/4)))
  }

  for (let i = 0; i > -cols; i--) {
    const pos = roundToPrecision(i * snapX)
    const two = roundToPrecision(pos + (snapX/2))
    validPos1.x.push(pos)
    validPos2.x.push(two)
    validPixel1.x.push(pos)
    validPixel2.x.push(two)
  }

  validPixel1.x = validPos1.x
  validPixel2.x = validPos2.x
  validSmall3.x = validSmall1.x
  validSmall4.x = validSmall2.x

  for (let j = 0; j <= (rows*2); j++) {
    const pos = roundToPrecision(j * snapY)
    const two = roundToPrecision(pos + (snapY/2))
    validPos1.y.push(pos)
    validPos2.y.push(two)
    validPixel1.y.push(roundToPrecision(pos + (snapY/2)))
    validPixel2.y.push(roundToPrecision(two + (snapY/2)))
    validSmall1.y.push(roundToPrecision(pos + (snapY/4)))
    validSmall2.y.push(roundToPrecision(two + (snapY/4)))
    validSmall3.y.push(roundToPrecision(pos - (snapY/4)))
    validSmall4.y.push(roundToPrecision(two - (snapY/4)))
  }

  for (let j = 0; j > -rows; j--) {
    const pos = roundToPrecision(j * snapY)
    const two = roundToPrecision(pos + (snapY/2))
    validPos1.y.push(pos)
    validPos2.y.push(two)
    validPixel1.y.push(roundToPrecision(pos + (snapY/2)))
    validPixel2.y.push(roundToPrecision(two + (snapY/2)))
  }

  return [validPos1, validPos2, validPixel1, validPixel2, validSmall1, validSmall2, validSmall3, validSmall4]
}

const calculateValid45degBitPosition = () => {
  const BIT_SHAPE = getShapeById('Bit45deg')
  const cols = gridWidth.value / placementSnap.value.x
  const rows = gridHeight.value / placementSnap.value.y
  const bitPaddingX = placementSnap.value.x * 4
  const bitPaddingY = placementSnap.value.y * 4
  const bitSnapX = (bitPaddingX / 3) + (BIT_SHAPE.w * Math.sqrt(2))
  const bitSnapY = (bitPaddingY / 3) + (BIT_SHAPE.h * Math.sqrt(2))
  const validBit1 = { x: [], y: [] }
  const validBit2 = { x: [], y: [] }

  for (let i = 0; i <= (cols/2); i++) {
    const pos = (i * bitPaddingX)
    const two = pos + (bitPaddingX / 2)
    validBit1.x.push(pos - bitSnapX)
    validBit1.x.push(pos)
    validBit1.x.push(pos + bitSnapX)

    validBit2.x.push(two - bitSnapX)
    validBit2.x.push(two)
    validBit2.x.push(two + bitSnapX)
  }

  for (let j = 0; j <= (rows/2); j++) {
    const offsetY = bitPaddingY / 2
    const pos = (j * bitPaddingY) + offsetY
    const two = pos + offsetY
    validBit1.y.push(pos - bitSnapY)
    validBit1.y.push(pos)
    validBit1.y.push(pos + bitSnapY)

    validBit2.y.push(two - bitSnapY)
    validBit2.y.push(two)
    validBit2.y.push(two + bitSnapY)
  }

  return [validBit1, validBit2]
}

const calculateValidSpacedHexPosition = () => {
  const HEX_SHAPE = getShapeById('HexSpaced')
  const offsetX = HEX_SHAPE.w / 2
  const offsetY = HEX_SHAPE.h / 2
  const paddingX = offsetX * 3.37
  const paddingY = offsetY * 2.254
  const cols = gridWidth.value / paddingX
  const rows = gridHeight.value / paddingY
  const validPos1 = { x: [], y: [] }
  const validPos2 = { x: [], y: [] }

  for (let i = 0; i <= (cols*2); i++) {
    const pos = (i * paddingX) + offsetX
    const two = pos + (paddingX/2)
    validPos1.x.push(pos)
    validPos2.x.push(two)
  }

  for (let i = 0; i > -cols; i--) {
    const pos = (i * paddingX) + offsetX
    const two = pos + (paddingX/2)
    validPos1.x.push(pos)
    validPos2.x.push(two)
  }

  for (let j = 0; j <= (rows*2); j++) {
    const pos = (j * paddingY) + offsetY
    const two = pos + (paddingY/2)
    validPos1.y.push(pos)
    validPos2.y.push(two)
  }

  for (let j = 0; j > -rows; j--) {
    const pos = (j * paddingY) + offsetY
    const two = pos + (paddingY/2)
    validPos1.y.push(pos)
    validPos2.y.push(two)
  }

  return [validPos1, validPos2]
}

const calculateValidSpacedDiamondPosition = () => {
  const DIAMOND_SHAPE = getShapeById('SmallDiamondSpaced')
  const offsetX = DIAMOND_SHAPE.w * 1.5
  const offsetY = DIAMOND_SHAPE.h / 2
  const paddingX = DIAMOND_SHAPE.w * 3.37
  const paddingY = offsetY * 2.254
  const cols = gridWidth.value / paddingX
  const rows = gridHeight.value / paddingY
  const internalOffsetX = 45
  const internalOffsetY = 26

  const verticalPos1 = { x: [], y: [] }
  const verticalPos2 = { x: [], y: [] }
  const topPos1 = { x: [], y: [] }
  const topPos2 = { x: [], y: [] }
  const bottomPos1 = { x: [], y: [] }
  const bottomPos2 = { x: [], y: [] }

  for (let i = 0; i <= (cols*2); i++) {
    const pos = (i * paddingX) + offsetX
    const two = pos + (paddingX/2)
    verticalPos1.x.push(pos)
    verticalPos2.x.push(two)

    topPos1.x.push(pos - internalOffsetX)
    topPos2.x.push(two - internalOffsetX)

    bottomPos1.x.push(pos - internalOffsetX)
    bottomPos2.x.push(two - internalOffsetX)
  }

  for (let i = 0; i > -cols; i--) {
    const pos = (i * paddingX) + offsetX
    const two = pos + (paddingX/2)
    verticalPos1.x.push(pos)
    verticalPos2.x.push(two)

    topPos1.x.push(pos - internalOffsetX)
    topPos2.x.push(two - internalOffsetX)

    bottomPos1.x.push(pos - internalOffsetX)
    bottomPos2.x.push(two - internalOffsetX)
  }

  for (let j = 0; j <= (rows*2); j++) {
    const pos = (j * paddingY) + offsetY
    const two = pos + (paddingY/2)
    verticalPos1.y.push(pos)
    verticalPos2.y.push(two)

    topPos1.y.push(pos - internalOffsetY)
    topPos2.y.push(two - internalOffsetY)

    bottomPos1.y.push(pos + internalOffsetY)
    bottomPos2.y.push(two + internalOffsetY)
  }

  for (let j = 0; j > -rows; j--) {
    const pos = (j * paddingY) + offsetY
    const two = pos + (paddingY/2)
    verticalPos1.y.push(pos)
    verticalPos2.y.push(two)

    topPos1.y.push(pos - internalOffsetY)
    topPos2.y.push(two - internalOffsetY)

    bottomPos1.y.push(pos + internalOffsetY)
    bottomPos2.y.push(two + internalOffsetY)
  }

  return [verticalPos1, verticalPos2, topPos1, topPos2, bottomPos1, bottomPos2]
}

const getSnapGridPositionDefault = (x, y) => {
  const relativeX = (x - startOffset.value)
  const relativeY = (y - startOffset.value)
  const gridSnapX = Math.round(relativeX / placementSnap.value.x) * placementSnap.value.x
  const gridSnapY = Math.round(relativeY / placementSnap.value.y) * placementSnap.value.y

  const snappedX = startOffset.value + gridSnapX
  const snappedY = startOffset.value + gridSnapY

  return {
    x: snappedX,
    y: snappedY,
  }
}

const getSnapWoodSlatPosition = (x, y, shape) => {
  // While dragging, the moving slats are still in shapeList; skip them.
  const movingIds = selectedTool.value === SELECTABLE_TOOLS.SELECT
    ? new Set(selectedItemsOnGrid.value.map(item => item.id))
    : new Set()
  const neighbours = shapeList.value
    .filter(item => isWoodSlatShapeId(item.metadata?.shape_id) && !movingIds.has(item.id))
    .map(item => ({ x: item.x, y: item.y, ...slatFootprint(item) }))
  return snapWoodSlatPosition(
    { x, y },
    { w: shape.w, h: shape.h },
    { origin: startOffset.value, stepX: gridPattern.value.snap.x, stepY: gridPattern.value.snap.y },
    neighbours,
  )
}

const getSnapSquareGridPosition = (x, y, shape) => {
  if (isWoodSlatShapeId(shape?.id)) {
    return getSnapWoodSlatPosition(x, y, shape)
  }

  const snap = getSnapGridPositionDefault(x, y)
  const smallShapes = ['Pixel', 'MiniShiplapVertical', 'MiniShiplapHorizontal', 'Bit']

  if (!shape || !smallShapes.includes(shape.id)) {
    return snap
  }

  if (shape.id === 'Bit') {
    return handleBit()
  }

  if (shape.id === 'Pixel') {
    // Shape position is (cell_left + w/2, cell_top + h/2). Subtract offset to get cell
    // origin, snap that, then add offset back so drag and temp-item use same semantics.
    const ox = shape.w / 2
    const oy = shape.h / 2
    const cellSnap = getSnapGridPositionDefault(x - ox, y - oy)
    return { x: cellSnap.x + ox, y: cellSnap.y + oy }
  }

  if (shape.id === 'MiniShiplapVertical' || (shape.id === 'MiniShiplapHorizontal' && shape.rotation === 90)) {
    const ox = shape.h / 2
    const cellSnap = getSnapGridPositionDefault(x - ox, y)
    return { x: cellSnap.x + ox, y: cellSnap.y }
  }

  if (shape.id === 'MiniShiplapHorizontal' || (shape.id === 'MiniShiplapVertical' && shape.rotation === 90)) {
    const oy = shape.h / 2
    const cellSnap = getSnapGridPositionDefault(x, y - oy)
    return { x: cellSnap.x, y: cellSnap.y + oy }
  }

  function handleBit() {
    // Bit uses a different grid; its position is (cell_x - w/2, cell_y + h/2). Use same
    // subtract-snap-add pattern so dragging doesn't jump.
    const ox = shape.w / 2
    const oy = shape.h / 2
    const relativeX = (x + ox) - startOffset.value
    const relativeY = (y - oy) - startOffset.value
    const snapX = (placementSnap.value.x * 2) / 3
    const snapY = (placementSnap.value.y * 2) / 3
    const bitGridSnapX = Math.round(relativeX / snapX) * snapX
    const bitGridSnapY = Math.round(relativeY / snapY) * snapY

    return {
      x: startOffset.value + bitGridSnapX - ox,
      y: startOffset.value + bitGridSnapY + oy
    }
  }

  return snap
}

// Optimized 45-degree grid snapping system
const createOptimized45degSnapping = () => {
  // Binary search tree for efficient position lookups
  class PositionTree {
    constructor() {
      this.root = null
    }

    insert(value) {
      this.root = this._insertRec(this.root, value)
    }

    _insertRec(node, value) {
      if (node === null) {
        return { value, left: null, right: null }
      }
      if (value < node.value) {
        node.left = this._insertRec(node.left, value)
      } else if (value > node.value) {
        node.right = this._insertRec(node.right, value)
      }
      return node
    }

    findClosest(target) {
      if (!this.root) return null
      return this._findClosestRec(this.root, target, this.root.value)
    }

    _findClosestRec(node, target, closest) {
      if (!node) return closest

      if (Math.abs(node.value - target) < Math.abs(closest - target)) {
        closest = node.value
      }

      if (target < node.value) {
        return this._findClosestRec(node.left, target, closest)
      } else if (target > node.value) {
        return this._findClosestRec(node.right, target, closest)
      }
      return closest
    }
  }

  // Spatial hash for quick position mapping
  class SpatialHash {
    constructor(cellSize = 10) {
      this.cellSize = cellSize
      this.grid = new Map()
    }

    hash(x, y) {
      const cellX = Math.floor(x / this.cellSize)
      const cellY = Math.floor(y / this.cellSize)
      return `${cellX},${cellY}`
    }

    add(x, y, data) {
      const key = this.hash(x, y)
      if (!this.grid.has(key)) {
        this.grid.set(key, [])
      }
      this.grid.get(key).push({ x, y, data })
    }

    getNearby(x, y, radius = this.cellSize) {
      const results = []
      const centerCellX = Math.floor(x / this.cellSize)
      const centerCellY = Math.floor(y / this.cellSize)
      const cellRadius = Math.ceil(radius / this.cellSize)

      for (let dx = -cellRadius; dx <= cellRadius; dx++) {
        for (let dy = -cellRadius; dy <= cellRadius; dy++) {
          const key = `${centerCellX + dx},${centerCellY + dy}`
          const cell = this.grid.get(key)
          if (cell) {
            results.push(...cell)
          }
        }
      }
      return results
    }
  }

  // Memoization cache - increased size for large grids
  const snapCache = new Map()
  const CACHE_SIZE_LIMIT = 5000 // Increased from 1000 for better performance on large grids

  const clearCache = () => {
    if (snapCache.size > CACHE_SIZE_LIMIT) {
      // More efficient cache clearing - keep the most recently used entries
      const entries = Array.from(snapCache.entries())
      const entriesToKeep = entries.slice(-Math.floor(CACHE_SIZE_LIMIT * 0.7)) // Keep 70% of newest entries
      snapCache.clear()
      entriesToKeep.forEach(([key, value]) => snapCache.set(key, value))
    }
  }

  // Pre-computed position trees for each shape type
  const positionTrees = {
    main: { x: new PositionTree(), y: new PositionTree() },
    pixel: { x: new PositionTree(), y: new PositionTree() },
    small: { x: new PositionTree(), y: new PositionTree() },
    bit: { x: new PositionTree(), y: new PositionTree() }
  }

  // Spatial hash for quick position lookups
  const spatialHash = new SpatialHash(20)

  // Build optimized data structures from valid positions
  const buildOptimizedStructures = () => {
    // Clear existing structures
    Object.values(positionTrees).forEach(tree => {
      tree.x = new PositionTree()
      tree.y = new PositionTree()
    })
    spatialHash.grid.clear()
    snapCache.clear()

    if (!valid45degPositions.value.length) return

    // Build position trees
    const positions = [
      { type: 'main', data: [valid45degPositions.value[0], valid45degPositions.value[1]] },
      { type: 'pixel', data: [valid45degPositions.value[2], valid45degPositions.value[3]] },
      { type: 'small', data: [valid45degPositions.value[4], valid45degPositions.value[5], valid45degPositions.value[6], valid45degPositions.value[7]] }
    ]

    positions.forEach(({ type, data }) => {
      data.forEach(pos => {
        pos.x.forEach(x => {
          positionTrees[type].x.insert(x)
          spatialHash.add(x, 0, { type, axis: 'x', value: x })
        })
        pos.y.forEach(y => {
          positionTrees[type].y.insert(y)
          spatialHash.add(0, y, { type, axis: 'y', value: y })
        })
      })
    })

    // Build bit position trees
    if (valid45degBitPositions.value.length) {
      valid45degBitPositions.value.forEach(pos => {
        pos.x.forEach(x => {
          positionTrees.bit.x.insert(x)
          spatialHash.add(x, 0, { type: 'bit', axis: 'x', value: x })
        })
        pos.y.forEach(y => {
          positionTrees.bit.y.insert(y)
          spatialHash.add(0, y, { type: 'bit', axis: 'y', value: y })
        })
      })
    }
  }

  // Optimized snapping function
  const getOptimizedSnapPosition = (x, y, shape) => {
    const relativeX = x - startOffset.value
    const relativeY = y - startOffset.value

    // Create cache key
    const cacheKey = `${Math.round(relativeX)},${Math.round(relativeY)},${shape?.id || 'default'}`

    // Check cache first
    if (snapCache.has(cacheKey)) {
      return snapCache.get(cacheKey)
    }

    // Determine which position trees to use
    let treeType = 'main'
    switch (shape?.id) {
      case 'Pixel45deg':
        treeType = 'pixel'
        break
      case 'MiniShiplapVertical45deg':
      case 'MiniShiplapHorizontal45deg':
        treeType = 'small'
        break
      case 'Bit45deg':
        treeType = 'bit'
        break
    }

    // Find closest positions using binary search trees
    const closestX = positionTrees[treeType].x.findClosest(relativeX)
    const closestY = positionTrees[treeType].y.findClosest(relativeY)

    // Calculate distances for all possible combinations
    const nearbyX = spatialHash.getNearby(relativeX, 0, 50)
    const nearbyY = spatialHash.getNearby(0, relativeY, 50)

    // Get all relevant positions for this shape type
    const relevantX = nearbyX.filter(p => p.data.type === treeType).map(p => p.data.value)
    const relevantY = nearbyY.filter(p => p.data.type === treeType).map(p => p.data.value)

    // Find the best combination
    let bestDistance = Infinity
    let bestX = closestX
    let bestY = closestY

    for (const posX of relevantX) {
      for (const posY of relevantY) {
        const distance = Math.sqrt(Math.pow(posX - relativeX, 2) + Math.pow(posY - relativeY, 2))
        if (distance < bestDistance) {
          bestDistance = distance
          bestX = posX
          bestY = posY
        }
      }
    }

    // Round to precision
    const roundToPrecision = (value, precision = 2) => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision)
    }

    const result = {
      x: roundToPrecision(startOffset.value + bestX),
      y: roundToPrecision(startOffset.value + bestY)
    }

    // Cache the result
    snapCache.set(cacheKey, result)
    clearCache()

    return result
  }

  return {
    buildOptimizedStructures,
    getOptimizedSnapPosition
  }
}

// Initialize optimized snapping system
const optimized45degSnapping = createOptimized45degSnapping()

// Replace the original getSnap45degGridPosition with optimized version
const getSnap45degGridPosition = (x, y, shape) => {
  return optimized45degSnapping.getOptimizedSnapPosition(x, y, shape)
}

const getSnapHexSpacedGridPosition = (x, y, shape) => {
  const pos = (shape.id === 'HexSpaced') ? validSpacedHexPositions.value : validSpacedDiamondPositions.value
  const relativeX = (x - startOffset.value)
  const relativeY = (y - startOffset.value)

  const findClosest = (validPositions, relativeValue) => {
    return validPositions.reduce((prev, curr) =>
      Math.abs(curr - relativeValue) < Math.abs(prev - relativeValue) ? curr : prev
    )
  }

  // Find closest points from all 6 arrays
  const closestPoints = pos.map(validPos => ({
    x: findClosest(validPos.x, relativeX),
    y: findClosest(validPos.y, relativeY)
  }))

  // Calculate distances to all possible points
  const distances = closestPoints.map(point =>
    Math.sqrt(Math.pow(point.x - relativeX, 2) + Math.pow(point.y - relativeY, 2))
  )

  // Find the index of the closest point
  const closestIndex = distances.reduce((minIndex, distance, currentIndex) =>
    distance < distances[minIndex] ? currentIndex : minIndex, 0
  )

  let rotation

  if (shape.id === 'HexSpaced') {
    rotation = shape.rotation
  } else if (closestIndex === 0 || closestIndex === 1) {
    rotation = 0
  } else if (closestIndex === 2 || closestIndex === 3) {
    rotation = 60
  } else if (closestIndex === 4 || closestIndex === 5) {
    rotation = 120
  }

  return {
    x: startOffset.value + closestPoints[closestIndex].x,
    y: startOffset.value + closestPoints[closestIndex].y,
    rotation,
  }
}

const getSnap3DGridPosition = (x, y, shape) => {
  const snap = getSnapGridPositionDefault(x, y)

  if (!shape || !showDepthPreview.value || !shape.d) {
    return snap
  }

  snap.x += shape.d
  snap.y += shape.d

  return snap
}

const getSnapGridPosition = (x, y, shape) => {
  let snap
  switch (gridPattern.value.id) {
    case GRID_PATTERN_IDS.SQUARE:
      snap = getSnapSquareGridPosition(x, y, shape)
      break
    case GRID_PATTERN_IDS.FREEFORM:
      snap = { x, y }
      break
    case GRID_PATTERN_IDS.SQUARE_45:
      snap = getSnap45degGridPosition(x, y, shape)
      break
    case GRID_PATTERN_IDS.HEX_SPACED:
      snap = getSnapHexSpacedGridPosition(x, y, shape)
      break
    case GRID_PATTERN_IDS.THREE_D:
      snap = getSnap3DGridPosition(x, y, shape)
      break
    default:
      snap = getSnapGridPositionDefault(x, y)
  }

  return snap
}

// ============================================================================
// Auto-expand-grid helpers
// ============================================================================
//
// These project Konva/Pinia state into the pure PlacementFootprintCell shape
// consumed by `composables/grid/placement.ts`. They are kept here (not in the
// store or the utility) because they need to read both Konva refs and the
// reactive grid store.

/** Project a temp item into grid-relative cells for the candidate search. */
const tempItemsToFootprintCells = () => {
  if (!tempNewItems.value || tempNewItems.value.length === 0) return []
  // Find the leader (the first item whose position is the snap origin).
  const leader = tempNewItems.value[0]
  if (!leader) return []
  const leaderX = leader.x
  const leaderY = leader.y
  return tempNewItems.value.map((it) => {
    const isTurned = Math.abs((it.rotation || 0) % 180) === 90
    const w = isTurned ? (it.height || it.h || 0) : (it.width || it.w || 0)
    const h = isTurned ? (it.width || it.w || 0) : (it.height || it.h || 0)
    return {
      x: (it.x || 0) - leaderX,
      y: (it.y || 0) - leaderY,
      width: w,
      height: h,
      rotation: it.rotation || 0,
    }
  })
}

/** Project committed Konva nodes (from `drawnShapesForCollision`) into cells. */
const drawnShapesToCommittedCells = () => {
  const cells = []
  for (const node of drawnShapesForCollision.value) {
    if (!node) continue
    const x = node.x()
    const y = node.y()
    // For axis-aligned rects, width/height come straight from the node.
    // For non-rect shapes we fall back to a generous AABB. The candidate
    // search only needs a coarse collision estimate.
    let w = (node.width && node.width()) || 0
    let h = (node.height && node.height()) || 0
    if ((!w || !h) && node.getClientRect) {
      try {
        const r = node.getClientRect({ skipTransform: false })
        w = r.width
        h = r.height
      } catch {
        // ignore; we'll just skip this shape
        continue
      }
    }
    if (!w || !h) continue
    const relX = x - startOffset.value
    const relY = y - startOffset.value
    cells.push({ x: relX, y: relY, width: w, height: h, rotation: node.rotation?.() || 0 })
  }
  return cells
}

/** Build the placement context for the current grid pattern. */
const buildPlacementContext = () => {
  const snap = placementSnap.value
  if (!snap) return null
  return {
    gridWidth: store.gridWidth,
    gridHeight: store.gridHeight,
    startOffset: store.startOffset,
    snap,
    is45Square: gridPattern.value?.id === GRID_PATTERN_IDS.SQUARE_45,
    isLayered: Boolean(selectedShape.value && (
      selectedShape.value.finishFamily === 'layered-engraved' ||
      /^LayeredEngraved/.test(selectedShape.value.id || '')
    )),
    maxGridWidth: MAX_GRID_WIDTH_PX,
    maxGridHeight: MAX_GRID_HEIGHT_PX,
  }
}

/** Recompute the candidate list. Called debounced from `moveTempItem`. */
const recomputeDropCandidates = () => {
  if (selectedTool.value !== SELECTABLE_TOOLS.ADD) {
    dropCandidates.value = []
    return
  }
  if (!mouseEvent.value) {
    dropCandidates.value = []
    return
  }
  const template = tempItemsToFootprintCells()
  if (template.length === 0) {
    dropCandidates.value = []
    return
  }
  const ctx = buildPlacementContext()
  if (!ctx) {
    dropCandidates.value = []
    return
  }
  // Cursor in absolute canvas coords, but the candidate utility expects
  // grid-relative coordinates — subtract startOffset.
  const cursorRel = {
    x: mouseEvent.value.x / gridZoom.value - ctx.startOffset,
    y: mouseEvent.value.y / gridZoom.value - ctx.startOffset,
  }
  const committed = drawnShapesToCommittedCells()
  const cands = findValidDropCandidates(
    template,
    cursorRel,
    committed,
    ctx,
    8, // cap to 8 to avoid visual noise on large grids
  )
  // Project back to absolute canvas coords for rendering.
  dropCandidates.value = cands.map((c) => {
    const minX = Math.min(...c.cells.map(cell => cell.x))
    const minY = Math.min(...c.cells.map(cell => cell.y))
    const maxX = Math.max(...c.cells.map(cell => cell.x + cell.width))
    const maxY = Math.max(...c.cells.map(cell => cell.y + cell.height))
    return {
      x: ctx.startOffset + minX,
      y: ctx.startOffset + minY,
      w: maxX - minX,
      h: maxY - minY,
    }
  })
}

/** Schedule a debounced candidate recompute (100ms). */
const scheduleCandidateRecompute = () => {
  if (candidateTimer) clearTimeout(candidateTimer)
  candidateTimer = setTimeout(recomputeDropCandidates, 100)
}

/**
 * Live auto-grow: while the user is dragging a shape, grow the grid so
 * the shape has room where the cursor is. Triggers only when BOTH:
 *   1. the cursor is inside the grid rectangle, AND
 *   2. the dragged shape's bounding box extends past the right or
 *      bottom edge of the grid.
 * This keeps the grid from "chasing" the cursor while the user is
 * still in the shape picker or just hovering off-canvas.
 */
const scheduleLiveAutoGrow = () => {
  if (!tempNewItems.value || tempNewItems.value.length === 0) return
  if (!mouseEvent.value) return
  // Build absolute-canvas cells from the current temp positions.
  // For layered engraved shapes, Konva stores the position at the
  // CENTER of the tile (offsetX/offsetY = w/2, h/2), but our
  // bounding-box math expects the TOP-LEFT. Subtract half the
  // dimensions for layered shapes so the cell rect is correct.
  const isLayered = Boolean(selectedShape.value && (
    selectedShape.value.finishFamily === 'layered-engraved' ||
    /^LayeredEngraved/.test(selectedShape.value.id || '')
  ))
  const cellsAbs = tempNewItems.value.map((it) => {
    const isTurned = Math.abs((it.rotation || 0) % 180) === 90
    const w = isTurned ? (it.height || it.h || 0) : (it.width || it.w || 0)
    const h = isTurned ? (it.width || it.w || 0) : (it.height || it.h || 0)
    const offsetX = isLayered ? w / 2 : 0
    const offsetY = isLayered ? h / 2 : 0
    return { x: (it.x || 0) - offsetX, y: (it.y || 0) - offsetY, width: w, height: h, rotation: it.rotation || 0 }
  })
  // The store action expects cells in GRID-RELATIVE coordinates (it
  // re-adds startOffset internally to convert back to absolute).
  const cellsRel = cellsAbs.map((c) => ({
    x: c.x - startOffset.value,
    y: c.y - startOffset.value,
    width: c.width,
    height: c.height,
    rotation: c.rotation,
  }))
  // Compute cursor position in canvas coordinates. mouseEvent.value
  // is the native MouseEvent (clientX/clientY = viewport pixels), so
  // we need to convert through the stage: subtract the stage
  // container's left/top, then the stage pan, then divide by zoom.
  const stage = stageRef.value?.getStage?.()
  let cursorAbs = { x: 0, y: 0 }
  if (stage) {
    const pointerPos = stage.getPointerPosition()
    const stagePos = stage.position()
    if (pointerPos) {
      cursorAbs = {
        x: (pointerPos.x - stagePos.x) / gridZoom.value,
        y: (pointerPos.y - stagePos.y) / gridZoom.value,
      }
    }
  }

  // Size the growth step to the leader tile so a 4ft layered set grows
  // in 4ft jumps while a 1ft single tile grows in 1ft jumps. The snap
  // grid (60px) acts as a floor.
  const leader = tempNewItems.value[0]
  let stepX = 60
  let stepY = 60
  if (leader) {
    const isTurned = Math.abs((leader.rotation || 0) % 180) === 90
    const w = isTurned ? (leader.height || leader.h || 0) : (leader.width || leader.w || 0)
    const h = isTurned ? (leader.width || leader.w || 0) : (leader.height || leader.h || 0)
    if (w > stepX) stepX = w
    if (h > stepY) stepY = h
  }

  // Grow when the dragged shape is straddling the visible grid
  // boundary — at least one cell inside the lined grid area AND at
  // least one cell outside (past the right or bottom edge). The
  // cursor sits at the leader so it can be inside or outside the
  // grid; what matters is whether the shape itself is straddling
  // the edge.
  const gridLeft = startOffset.value
  const gridTop = startOffset.value
  const gridRight = startOffset.value + store.gridWidth
  const gridBottom = startOffset.value + store.gridHeight

  // Also require the cursor (in canvas coords) to be inside the
  // visible grid rectangle. This prevents growth when the user is
  // dragging the shape from the shape picker on the right side of
  // the studio.
  if (
    cursorAbs.x < gridLeft || cursorAbs.x > gridRight ||
    cursorAbs.y < gridTop || cursorAbs.y > gridBottom
  ) return

  let hasInside = false
  let hasOutside = false
  for (const c of cellsAbs) {
    const cRight = c.x + c.width
    const cBottom = c.y + c.height
    const inside =
      c.x < gridRight && cRight > gridLeft &&
      c.y < gridBottom && cBottom > gridTop
    const outside =
      cRight > gridRight || cBottom > gridBottom
    if (inside) hasInside = true
    if (outside) hasOutside = true
  }
  if (!hasInside || !hasOutside) return

  store.autoExpandGridForPlacement(cellsRel, cursorAbs, { x: stepX, y: stepY })
}

const moveTempItem = () => {
  if (!mouseEvent.value) return;

  if (selectedTool.value === SELECTABLE_TOOLS.ADD) {
    if (!selectedColor.value || !selectedShape.value) return;

    // Early exit if no temp items available yet
    if (!tempNewItems.value.length) {
      // Immediately create temp item for instant feedback
      store.setTempItemByCurrentSelection();
      return;
    }

    // Initialize or update initial positions if not set (for maintaining relative positions)
    if (!initialPositions.value.size) {
      tempNewItems.value.forEach(item => {
        initialPositions.value.set(item.id, {
          x: item.x,
          y: item.y,
          rotation: item.rotation || 0
        });
      });
    }

    // Calculate the base position using the first item as the leader
    const leaderItem = tempNewItems.value[0];
    if (!leaderItem) return;

    const leaderInitialPos = initialPositions.value.get(leaderItem.id);
    if (!leaderInitialPos) return;

    // Calculate the new position for the leader item using its actual shape
    const cachedPosition = calcMoveItemPosition(leaderItem);

    // Calculate the offset from initial position to new position
    const offsetX = cachedPosition.x - leaderInitialPos.x;
    const offsetY = cachedPosition.y - leaderInitialPos.y;

    // Apply the offset to all items while maintaining their relative positions
    tempNewItems.value.forEach((tempItem) => {
      const initialPos = initialPositions.value.get(tempItem.id);
      if (!initialPos) return;

      Object.assign(tempItem, tempItemAttrs, {
        x: initialPos.x + offsetX,
        y: initialPos.y + offsetY,
        rotation: (cachedPosition.rotation !== undefined) ? cachedPosition.rotation : initialPos.rotation,
      });
    });

    if ([CollisionLevel.ALL, CollisionLevel.NEW_OR_COPY].includes(gridPattern.value.collisionLevel)) {
      checkAllTempCollision();
    }
    // Recompute "where can I drop this?" candidates (debounced so we don't
    // thrash Konva on every pointermove).
    scheduleCandidateRecompute()
    // Live auto-grow: extend the grid as the cursor approaches the edge.
    scheduleLiveAutoGrow()
  } else if ([SELECTABLE_TOOLS.COPY, SELECTABLE_TOOLS.CUT].includes(selectedTool.value)) {
    const newItems = calcTempItemsPositionBasedOnPointer(tempNewItems)

    store.setTempNewItems(newItems);
    if ([CollisionLevel.ALL, CollisionLevel.NEW_OR_COPY].includes(gridPattern.value.collisionLevel)) {
      checkAllTempCollision();
    }
  }
};

const addTempItemsAsGridShape = (touch = false) => {
  // First-pass collision check: read the `collision: true` flag the temp
  // collision system set during `moveTempItem`. If there's a collision OR
  // the temp extends past the grid bounds, try to auto-grow the grid
  // toward the cursor before rejecting.
  const tempGroup = tempGroupRef.value?.getNode()
  if (tempGroup) {
    const hasCollision = tempGroup.children?.some(child => {
      if (child instanceof Konva.Group) {
        return child.children?.some(face => face.attrs.collision === true)
      }
      return child.attrs.collision === true
    })
    // Footprint cells in absolute canvas coords (so we can check the
    // local "is the shape past the edge" condition). The store action
    // expects cells in grid-relative coords, so we also build cellsRel.
    const cellsAbs = tempNewItems.value.map((it) => {
      const isTurned = Math.abs((it.rotation || 0) % 180) === 90
      const w = isTurned ? (it.height || it.h || 0) : (it.width || it.w || 0)
      const h = isTurned ? (it.width || it.w || 0) : (it.height || it.h || 0)
      return { x: it.x || 0, y: it.y || 0, width: w, height: h, rotation: it.rotation || 0 }
    })
    const cellsRel = cellsAbs.map((c) => ({
      x: c.x - startOffset.value,
      y: c.y - startOffset.value,
      width: c.width,
      height: c.height,
      rotation: c.rotation,
    }))

    if (hasCollision) {
      // Try to grow first. If still no room, the original toast fires.
      const cursorAbs = mouseEvent.value
        ? { x: mouseEvent.value.x / gridZoom.value, y: mouseEvent.value.y / gridZoom.value }
        : { x: 0, y: 0 }
      const grow = store.autoExpandGridForPlacement(cellsRel, cursorAbs)
      if (grow.capped) {
        Toast({ icon: 'warning', title: 'Grid is full', text: 'Maximum grid size reached.', customClass: { popup: 'swal-toast-vertical' } })
        return
      }
      // Re-evaluate collision in the (possibly) expanded grid. Clear the
      // existing flag, re-run the per-temp collision check, and see if any
      // tile is still flagged.
      tempGroup.children?.forEach(child => {
        if (child instanceof Konva.Group) {
          child.children?.forEach(face => { if ('collision' in face.attrs) face.attrs.collision = false })
        } else if ('collision' in child.attrs) {
          child.attrs.collision = false
        }
      })
      checkAllTempCollision()
      const stillColliding = tempGroup.children?.some(child => {
        if (child instanceof Konva.Group) {
          return child.children?.some(face => face.attrs.collision === true)
        }
        return child.attrs.collision === true
      })
      if (stillColliding) {
        Toast({ icon: 'warning', title: 'Tiles cannot overlap', text: 'Please place the tile in an empty spot', customClass: { popup: 'swal-toast-vertical' } })
        return
      }
    } else {
      // No collision, but the footprint might still extend past the grid
      // (e.g. dropped right at the edge). Grow to fit so the placement
      // succeeds.
      const cursorAbs = mouseEvent.value
        ? { x: mouseEvent.value.x / gridZoom.value, y: mouseEvent.value.y / gridZoom.value }
        : { x: 0, y: 0 }
      const grow = store.autoExpandGridForPlacement(cellsRel, cursorAbs)
      if (grow.capped) {
        Toast({ icon: 'warning', title: 'Grid is full', text: 'Maximum grid size reached.', customClass: { popup: 'swal-toast-vertical' } })
        return
      }
    }
  }

  // Clear any existing selection when adding new tiles
  if (selectedItemsOnGrid.value.length > 0) {
    store.unselectItemsOnGrid()
  }

  // CRITICAL: Save state BEFORE making any changes for proper undo functionality
  store.saveState()

  // PERFORMANCE: Immediate operations for critical path
  // For mobile touch, we need to calculate the position with proper snapping
  let newItems;
  if (touch) {
    // Get pointer position for mobile
    const stage = stageRef.value.getStage();
    const pointerPos = stage.getPointerPosition();

    if (!pointerPos || tempNewItems.value.length === 0) {
      // Fallback if no pointer position or no temp items
      newItems = tempNewItems.value;
    } else {
      // Use calcMoveItemPosition for proper grid snapping with the leader item's shape
      const leaderItem = tempNewItems.value[0];
      const snappedPosition = calcMoveItemPosition(leaderItem || selectedShape.value);

      // Apply the LEADER'S MOVE to every item, not the leader's absolute
      // position.
      //
      // A shape sold in a set (a 3D box comes as 2, 4 or 8 tiles) is laid out
      // as several temp items, each offset from the first by the set's
      // arrangement - see calculateMultiShapePositions in CanvasGrid/helpers.
      // Overwriting every item's x/y with the leader's snapped position
      // collapsed the whole set onto a single tile: the second tile was drawn
      // exactly on top of the first, so the user saw ONE tile instead of the
      // set. The desktop path never had this problem because it commits the
      // temp items as they are.
      //
      // So the leader's own position is snapped, and every other item keeps
      // its offset from the leader by moving by the same delta. This is the
      // same delta moveTempItem applies while dragging.
      newItems = moveItemsByLeaderDelta(
        tempNewItems.value.map(item => ({
          ...item,
          rotation: snappedPosition.rotation !== undefined ? snappedPosition.rotation : item.rotation
        })),
        { x: snappedPosition.x, y: snappedPosition.y }
      );
    }
  } else {
    newItems = tempNewItems.value;
  }
  const timestamp = Date.now() // Single timestamp for all items

  // Group items by their existing set_id (for copied items) or by whether they need a new set_id
  const itemsBySetId = new Map()
  const itemsNeedingNewSetId = []

  // First pass: separate items that already have set_id from those that need one
  for (const item of newItems) {
    const existingMetadata = item.metadata || item.attrs?.metadata
    const existingSetId = existingMetadata?.set_id

    if (existingSetId) {
      // Item already has a set_id (from copy/paste) - preserve it
      if (!itemsBySetId.has(existingSetId)) {
        itemsBySetId.set(existingSetId, [])
      }
      const itemsInSet = itemsBySetId.get(existingSetId)
      if (itemsInSet) {
        itemsInSet.push(item)
      }
    } else {
      // Item doesn't have set_id - check if it needs one
      itemsNeedingNewSetId.push(item)
    }
  }

  // Generate set_id for items that need one (new items with quantity > 1)
  const quantity = selectedShape.value?.quantity || 1
  let newSetId = undefined
  if (itemsNeedingNewSetId.length > 0 && quantity > 1) {
    // Generate unique set_id for new items that need grouping
    newSetId = `set-${timestamp}-${Math.round(Math.random() * 10000)}`
    itemsBySetId.set(newSetId, itemsNeedingNewSetId)
  } else if (itemsNeedingNewSetId.length > 0) {
    // Items don't need a set_id (quantity = 1)
    itemsBySetId.set(null, itemsNeedingNewSetId)
  }

  // PERFORMANCE: Pre-allocate arrays and minimize object creation
  const shapesToAdd = new Array(newItems.length)
  const lastAddedIds = new Array(newItems.length)

  // PERFORMANCE: Cache tempResetedItemAttrs to avoid repeated access
  const resetAttrs = tempResetedItemAttrs

  let itemIndex = 0
  for (const [setId, itemsInSet] of itemsBySetId) {
    for (const tempItem of itemsInSet) {
      const generatedID = `${timestamp}-${itemIndex}`

      // Preserve existing set_id or use the newly generated one
      const existingMetadata = tempItem.metadata || tempItem.attrs?.metadata
      const updatedMetadata = existingMetadata ? {
        ...existingMetadata,
        ...(setId && { set_id: setId })
      } : (setId ? { set_id: setId } : undefined)

      // PERFORMANCE: Minimize object spread operations
      shapesToAdd[itemIndex] = {
        ...tempItem,
        x: tempItem.x,
        y: tempItem.y,
        id: generatedID,
        ...(updatedMetadata && { metadata: updatedMetadata }),
        ...resetAttrs,
        attrs: {
          ...tempItem.attrs,
          rotation: tempItem.rotation,
          id: generatedID,
          ...(updatedMetadata && { metadata: updatedMetadata }),
        }
      }
      lastAddedIds[itemIndex] = generatedID
      itemIndex++
    }
  }

  // PERFORMANCE: Single batch operation instead of multiple individual adds
  store.addShapes(shapesToAdd)
  store.expandGridToFitShapes()
  closeShapeGroup()

  if ([SELECTABLE_TOOLS.COPY, SELECTABLE_TOOLS.CUT].includes(selectedTool.value)) {
    store.pasteItems(lastAddedIds)
  }

  // CRITICAL: Create new temp item immediately (must stay synchronous)
  if (selectedTool.value === SELECTABLE_TOOLS.ADD && selectedColor.value && selectedShape.value) {
    store.setTempItemByCurrentSelection()

    // MOBILE FIX: Ensure animation is enabled for mobile after adding a tile
    if (touch && !isAnimating.value) {
      isAnimating.value = true
      // Also trigger immediate temp item movement for mobile
      nextTick(() => {
        moveTempItem()
      })
    }
  }

  // PERFORMANCE: Defer heavy operations to avoid blocking UI
  nextTick(() => {
    // Network request - definitely should be async
    saveProjectDesign()
  })
}

const getGridPosLimits = (pos = {x: 0, y: 0}) => {
  const relativeGridW = (gridWidth.value * gridZoom.value)
  const relativeGridH = (gridHeight.value * gridZoom.value)
  // The VISIBLE stage size, not the stage's full size: the stage is drawn
  // taller than the container so it can run under the mobile sheet, but the
  // extra band is hidden behind the sheet and must not count as room to
  // pan the grid into.
  const stageSize = {
    width: stageConfig.width,
    height: visibleStageSize.value.height,
  }

  // grid smaller than screen
  if (stageSize.width > relativeGridW && stageSize.height > relativeGridH) {
    pos.x = Math.min(Math.max(pos.x, 0), stageSize.width - relativeGridW)
    pos.y = Math.min(Math.max(pos.y, 0), stageSize.height - relativeGridH)

    return pos
  }

  // grid larger than screen
  const safeWspace = 320
  const safeHspace = 320
  const minPosX = -relativeGridW + safeWspace
  const minPosY = -relativeGridH + safeHspace
  const maxPosX = stageSize.width - safeWspace
  const maxPosY = stageSize.height - safeHspace

  if (pos.x < minPosX || pos.x > maxPosX) {
    pos.x = Math.min(Math.max(pos.x, minPosX), maxPosX)
  }

  if (pos.y < minPosY || pos.y > maxPosY) {
    pos.y = Math.min(Math.max(pos.y, minPosY), maxPosY)
  }

  return pos
}

const handleMouseEnter = (e) => {
  isAnimating.value = true;
  mouseEvent.value = e?.evt;

  // For ADD tool, ensure temp item is ready immediately when mouse enters
  if (selectedTool.value === SELECTABLE_TOOLS.ADD &&
      selectedColor.value && selectedShape.value &&
      !tempNewItems.value.length) {
    store.setTempItemByCurrentSelection();
  }
};

const handleMouseLeave = () => {
  isAnimating.value = false;
  mouseEvent.value = null;
};

const snapToGridIncrement = (size, gridSnap) => {
  // Snap to grid pattern increments for better alignment
  const snapSize = gridSnap * 2; // Use double snap for better granularity
  return Math.round(size / snapSize) * snapSize;
};

const getMinGridSize = () => {
  // Allow resizing to minimal size just above 0
  return { width: 50, height: 50 };
};

const handleGridResize = (e) => {
  if (!isResizingGrid.value) return;

  const stage = e.target.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();

  // Convert to grid coordinates
  const currentGridPos = {
    x: (pointerPos.x - stagePos.x) / gridZoom.value,
    y: (pointerPos.y - stagePos.y) / gridZoom.value
  };

  // Calculate delta from start position in grid coordinates
  const deltaX = currentGridPos.x - store.resizeStartPos.x;
  const deltaY = currentGridPos.y - store.resizeStartPos.y;

  // Calculate new grid size based on which edges are being resized
  let newWidth = store.resizeStartSize.width;
  let newHeight = store.resizeStartSize.height;

  if (hoveredEdges.value.includes('right')) {
    newWidth = store.resizeStartSize.width + deltaX;
    // Snap to grid increments for better alignment
    if (gridPattern.value?.snap?.x) {
      newWidth = snapToGridIncrement(newWidth, gridPattern.value.snap.x);
    }
  }
  if (hoveredEdges.value.includes('bottom')) {
    newHeight = store.resizeStartSize.height + deltaY;
    // Snap to grid increments for better alignment
    if (gridPattern.value?.snap?.y) {
      newHeight = snapToGridIncrement(newHeight, gridPattern.value.snap.y);
    }
  }

  // Apply constraints
  const minSize = getMinGridSize();
  newWidth = Math.max(newWidth, minSize.width);
  newHeight = Math.max(newHeight, minSize.height);

  // Apply maximum constraints to prevent excessive sizes
  newWidth = Math.min(newWidth, 5000);
  newHeight = Math.min(newHeight, 5000);

  // Update grid size in real-time
  store.setGridSize(newWidth, newHeight);
};

// Mobile-specific grid resize function that takes grid coordinates directly
const handleMobileGridResize = (currentGridPos) => {
  if (!isResizingGrid.value) return;

  // Calculate delta from start position in grid coordinates
  const deltaX = currentGridPos.x - store.resizeStartPos.x;
  const deltaY = currentGridPos.y - store.resizeStartPos.y;

  // Calculate new grid size based on which edges are being resized
  let newWidth = store.resizeStartSize.width;
  let newHeight = store.resizeStartSize.height;

  if (hoveredEdges.value.includes('right')) {
    newWidth = store.resizeStartSize.width + deltaX;
    // Snap to grid increments for better alignment
    if (gridPattern.value?.snap?.x) {
      newWidth = snapToGridIncrement(newWidth, gridPattern.value.snap.x);
    }
  }
  if (hoveredEdges.value.includes('bottom')) {
    newHeight = store.resizeStartSize.height + deltaY;
    // Snap to grid increments for better alignment
    if (gridPattern.value?.snap?.y) {
      newHeight = snapToGridIncrement(newHeight, gridPattern.value.snap.y);
    }
  }

  // Apply constraints
  const minSize = getMinGridSize();
  newWidth = Math.max(newWidth, minSize.width);
  newHeight = Math.max(newHeight, minSize.height);

  // Apply maximum constraints to prevent excessive sizes
  newWidth = Math.min(newWidth, 5000);
  newHeight = Math.min(newHeight, 5000);

  // Update grid size in real-time
  store.setGridSize(newWidth, newHeight);
};

const handleGridEdgeDetection = (e) => {
  // Skip edge detection on touch devices (they use always-visible handles)
  if (isTouchDevice.value || isResizingGrid.value) return;

  const allowedTools = [SELECTABLE_TOOLS.SELECT, SELECTABLE_TOOLS.ADD];
  if (!allowedTools.includes(selectedTool.value)) {
    store.setHoveredEdges([]);
    return;
  }

  const stage = e.target.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();

  // Convert mouse position to grid coordinates (accounting for zoom and pan)
  const mousePos = {
    x: (pointerPos.x - stagePos.x) / gridZoom.value,
    y: (pointerPos.y - stagePos.y) / gridZoom.value
  };

  // Calculate grid bounds in grid coordinates
  const gridBounds = {
    left: startOffset.value,
    top: startOffset.value,
    right: startOffset.value + gridWidth.value,
    bottom: startOffset.value + gridHeight.value,
    width: gridWidth.value,
    height: gridHeight.value
  };

  // Detect which edges are being hovered (with larger threshold for easier interaction)
  const threshold = 15; // Larger threshold in grid coordinates for easier targeting
  const edges = [];

  if (Math.abs(mousePos.x - gridBounds.right) <= threshold) {
    edges.push('right');
  }
  if (Math.abs(mousePos.y - gridBounds.bottom) <= threshold) {
    edges.push('bottom');
  }

  store.setHoveredEdges(edges);
};

const handleMouseMove = (e) => {
  // Handle ruler pan drag
  if (rulerDragging.value) {
    const stageNode = e.target.getStage()
    if (stageNode) {
      const pointerPos = stageNode.getPointerPosition()
      if (pointerPos) {
        const dx = pointerPos.x - rulerDragStart.value.x
        const dy = pointerPos.y - rulerDragStart.value.y
        stageNode.position({
          x: rulerStageStart.value.x + dx,
          y: rulerStageStart.value.y + dy,
        })
        store.gridPos = { x: rulerStageStart.value.x + dx, y: rulerStageStart.value.y + dy }
      }
    }
    return
  }

  // Handle grid resize if in progress
  if (isResizingGrid.value) {
    handleGridResize(e);
    return;
  }

  if (selectedTool.value === SELECTABLE_TOOLS.ADD) handleMouseMoveOnAdd(e);
  if (selectedTool.value === SELECTABLE_TOOLS.COPY) handleMouseMoveOnAdd(e);
  if (selectedTool.value === SELECTABLE_TOOLS.CUT) handleMouseMoveOnAdd(e);
  if (isDrawing.value && selectedTool.value === SELECTABLE_TOOLS.LASSO) handleMouseMoveOnLasso(e);
  if (isDrawing.value && selectedTool.value === SELECTABLE_TOOLS.RECT_SELECT) handleMouseMoveOnRect(e);

  // Handle grid edge detection for resize functionality
  handleGridEdgeDetection(e);
};

const handleMouseMoveOnAdd = (e) => {
  const allowedTools = [
    SELECTABLE_TOOLS.ADD,
    SELECTABLE_TOOLS.COPY,
    SELECTABLE_TOOLS.CUT,
  ];
  if (!allowedTools.includes(selectedTool.value)) return;
  mouseEvent.value = e.evt.constructor.name === "MouseEvent" ? e.evt : null;

  if (isAnimating.value) {
    requestAnimationFrame(moveTempItem);
  }
};

const handleMouseMoveOnLasso = (e) => {
  if (!isDrawing.value || selectedTool.value !== SELECTABLE_TOOLS.LASSO) return;

  const stage = e.target.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();

  const pos = {
    x: (pointerPos.x - stagePos.x) / gridZoom.value,
    y: (pointerPos.y - stagePos.y) / gridZoom.value,
  };

  const minDistance = Math.min(gridPattern.value?.snap?.x || 30, 15);
  if (getDistance(lastPoint.value, pos) >= minDistance) {
    pathPoints.value.push(pos.x, pos.y)
    lastPoint.value = pos

    // Throttle lasso updates to improve performance
    if (lassoThrottleId.value) {
      cancelAnimationFrame(lassoThrottleId.value);
    }

    lassoThrottleId.value = requestAnimationFrame(() => {
      store.setLassoSelectionItem({
        ...baseLassoConfig,
        points: [...pathPoints.value],
      });
      lassoThrottleId.value = null;
    });
  }
};

const handleMouseMoveOnRect = (e) => {
  if (!isDrawing.value || selectedTool.value !== SELECTABLE_TOOLS.RECT_SELECT) return;

  const stage = e.target.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();

  const pos = {
    x: (pointerPos.x - stagePos.x) / gridZoom.value,
    y: (pointerPos.y - stagePos.y) / gridZoom.value,
  };

  const startX = pathPoints.value[0];
  const startY = pathPoints.value[1];
  const width = pos.x - startX;
  const height = pos.y - startY;

  // Calculate the actual position and dimensions considering negative values
  const x = width < 0 ? startX + width : startX;
  const y = height < 0 ? startY + height : startY;
  const absWidth = Math.abs(width);
  const absHeight = Math.abs(height);

  store.setRectSelectionItem({
    ...baseRectConfig,
    x,
    y,
    width: absWidth,
    height: absHeight,
  });
};

const handleGridResizeMouseDown = (e) => {
  // Only start resize if we're hovering over edges
  if (hoveredEdges.value.length === 0) return false;

  const stage = e.target.getStage();
  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();

  // Convert to grid coordinates for consistent resize calculations
  const gridMousePos = {
    x: (pointerPos.x - stagePos.x) / gridZoom.value,
    y: (pointerPos.y - stagePos.y) / gridZoom.value
  };

  // Start grid resize
  store.startGridResize(gridMousePos);
  e.cancelBubble = true;
  return true;
};

const rulerDragging = ref(false)
const rulerDragStart = ref({ x: 0, y: 0 })
const rulerStageStart = ref({ x: 0, y: 0 })
// For mobile touch-and-hold on the ruler (Figma FR-62 mobile UX): we require
// the user to press and hold for ~300ms before the pan activates, so a
// brief tap doesn't accidentally pan the canvas.
let rulerHoldTimer = null
const RULER_HOLD_MS = 300

const isTouchPointer = (e) =>
  e?.evt?.pointerType === 'touch' || e?.evt?.type?.startsWith('touch')

const beginRulerDrag = (e) => {
  const stageNode = e.target.getStage()
  if (!stageNode) return false
  const pointerPos = stageNode.getPointerPosition()
  const stagePos = stageNode.position()
  if (!pointerPos) return false
  const x = (pointerPos.x - stagePos.x) / gridZoom.value
  const y = (pointerPos.y - stagePos.y) / gridZoom.value
  const rulerSize = store.startOffset
  if (x >= rulerSize && y >= rulerSize) return false
  rulerDragging.value = true
  rulerDragStart.value = { x: pointerPos.x, y: pointerPos.y }
  rulerStageStart.value = { x: stagePos.x, y: stagePos.y }
  return true
}

const handleMouseDown = (e) => {
  // Pan the grid when clicking and dragging on the ruler area.
  // On touch devices we wait for a brief hold before activating the drag,
  // so a tap on the ruler doesn't accidentally pan the canvas.
  const touch = isTouchPointer(e)
  if (touch) {
    if (rulerHoldTimer) clearTimeout(rulerHoldTimer)
    rulerHoldTimer = setTimeout(() => {
      rulerHoldTimer = null
      beginRulerDrag(e)
    }, RULER_HOLD_MS)
    return
  }
  if (beginRulerDrag(e)) return

  // Check if this is a grid resize operation first
  if (handleGridResizeMouseDown(e)) return;

  if (![SELECTABLE_TOOLS.LASSO, SELECTABLE_TOOLS.RECT_SELECT].includes(selectedTool.value)) return;

  const stage = e.target.getStage();
  if (!stage) return;

  const stagePos = stage.position();
  const pointerPos = stage.getPointerPosition();
  if (!pointerPos) return;

  const pos = {
    x: (pointerPos.x - stagePos.x) / gridZoom.value,
    y: (pointerPos.y - stagePos.y) / gridZoom.value,
  };

  // Reset before starting new selection
  if (selectedTool.value === SELECTABLE_TOOLS.LASSO) {
    resetLasso();
  } else {
    resetRect();
  }

  isDrawing.value = true;
  pathPoints.value = [pos.x, pos.y];
  lastPoint.value = pos;

  // Immediately show the lasso starting point for visual feedback
  if (selectedTool.value === SELECTABLE_TOOLS.LASSO) {
    // Initialize with a visible starting point immediately
    store.setLassoSelectionItem({
      ...baseLassoConfig,
      points: [pos.x, pos.y, pos.x + 1, pos.y + 1], // More visible line
      visible: true
    });
  } else if (selectedTool.value === SELECTABLE_TOOLS.RECT_SELECT) {
    // Initialize rect selection
    store.setRectSelectionItem({
      ...baseRectConfig,
      x: pos.x,
      y: pos.y,
      width: 1,
      height: 1,
      visible: true
    });
  }
};

const handleLassoMouseUp = () => {
  if (pathPoints.value.length < 4) return [];

  // Immediately close the lasso for instant visual feedback
  store.setLassoSelectionItem({
    ...baseLassoConfig,
    points: [...pathPoints.value],
    closed: true,
  });

  const stage = stageRef.value.getStage();
  const stagePos = stage.position();
  const rawChildren = shapesLayerRef.value?.getNode()?.children || [];
  const shapesLayer = shapesLayerRef.value?.getNode();

  // Pre-transform lasso points once (expensive operation)
  const transformedPoints = pathPoints.value.map((p, i) => {
    const isX = i % 2 === 0;
    const stageOffset = isX ? stagePos.x : stagePos.y;
    return p * gridZoom.value + stageOffset;
  });

  // Get lasso bounding box for spatial culling
  const lassoBounds = getLassoBoundingBox(transformedPoints);
  if (!lassoBounds) return [];

  const is3D = gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D && showDepthPreview.value;

  const candidateShapes = [];
  for (const child of rawChildren) {
    if (is3D) {
      const frontFace = getFrontFaceFromGroup(child);
      if (!frontFace) continue;
      const shapeId = typeof frontFace.id === 'function' ? frontFace.id() : frontFace.id;
      candidateShapes.push({
        shape: frontFace,
        id: shapeId,
        frontFace,
        group: child,
      });
    } else {
      if (!child || !child.id) continue;
      const childId = typeof child.id === 'function' ? child.id() : child.id;
      if (!childId || typeof childId !== 'string' || childId.endsWith('-label')) continue;
      candidateShapes.push({
        shape: child,
        id: childId,
        frontFace: child,
        group: null,
      });
    }
  }

  const selectedShapes = [];
  const selectedIds = [];

  // Clear current selection immediately for instant feedback
  store.unselectItemsOnGrid();

  // Process shapes with progressive UI updates
  const processShapesProgressive = (startIndex = 0) => {
    return new Promise((resolve) => {
      const batchSize = 15; // Smaller batches for more frequent updates
      const endIndex = Math.min(startIndex + batchSize, candidateShapes.length);
      const batchSelectedIds = [];

      // Process current batch
      for (let i = startIndex; i < endIndex; i++) {
        const candidate = candidateShapes[i];
        const frontRect = candidate.frontFace.getClientRect();
        const groupRect = candidate.group?.getClientRect ? candidate.group.getClientRect() : null;

        const shapeBounds = groupRect ? {
          minX: Math.min(frontRect.x, groupRect.x),
          minY: Math.min(frontRect.y, groupRect.y),
          maxX: Math.max(frontRect.x + frontRect.width, groupRect.x + groupRect.width),
          maxY: Math.max(frontRect.y + frontRect.height, groupRect.y + groupRect.height),
        } : {
          minX: frontRect.x,
          minY: frontRect.y,
          maxX: frontRect.x + frontRect.width,
          maxY: frontRect.y + frontRect.height,
        };

        // Skip shapes that don't intersect with lasso bounds
        if (!boundingBoxIntersects(lassoBounds, shapeBounds)) {
          continue;
        }

        // Only get corners for shapes that pass bounding box test
        const corners = getBoxCorners(frontRect);
        if (groupRect) {
          corners.push(...getBoxCorners(groupRect));
        }

        // Check if any corner is inside the lasso (early exit on first match)
        for (const corner of corners) {
          if (isPointInPolygon(corner, transformedPoints)) {
            selectedShapes.push(candidate.shape);
            selectedIds.push(candidate.id);
            batchSelectedIds.push(candidate.id);
            break; // Found one corner inside, no need to check others
          }
        }
      }

      // Update UI immediately with found shapes from this batch
      if (batchSelectedIds.length > 0) {
        // Add to existing selection
        store.selectItemsOnGrid([...selectedIds]);
        // Force Konva to redraw the layer for immediate visual feedback
        shapesLayer?.batchDraw();
      }

      // Continue processing if there are more shapes
      if (endIndex < candidateShapes.length) {
        requestAnimationFrame(() => {
          processShapesProgressive(endIndex).then(resolve);
        });
      } else {
        resolve(selectedShapes);
      }
    });
  };

  // Start progressive processing and return a promise
  return processShapesProgressive();
}

const handleRectSelectMouseUp = () => {
  const stage = stageRef.value.getStage()
  const stagePos = stage.position()
  const rect = store.rectSelectionItem
  let shapes = shapesLayerRef.value?.getNode()?.children || [];

  if (gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D && showDepthPreview.value) {
    shapes = shapes.map(group => getFrontFaceFromGroup(group)).filter(Boolean);
  } else {
    shapes = shapes.filter(child => {
      if (!child || !child.id) return false;
      const childId = typeof child.id === 'function' ? child.id() : child.id;
      return childId && typeof childId === 'string' && !childId.endsWith('-label');
    });
  }

  if (!rect) return

  const selectedShapes = shapes.filter((shape) => {
    const shapeBox = shape.getClientRect();
    const rectBox = {
      x: rect.x * gridZoom.value + stagePos.x,
      y: rect.y * gridZoom.value + stagePos.y,
      width: rect.width * gridZoom.value,
      height: rect.height * gridZoom.value,
    }

    return (
      shapeBox.x < rectBox.x + rectBox.width &&
      shapeBox.x + shapeBox.width > rectBox.x &&
      shapeBox.y < rectBox.y + rectBox.height &&
      shapeBox.y + shapeBox.height > rectBox.y
    )
  })

  return selectedShapes
}

const handleMouseUp = async () => {
  // Cancel any pending touch-and-hold on the ruler
  if (rulerHoldTimer) {
    clearTimeout(rulerHoldTimer)
    rulerHoldTimer = null
  }
  // Stop ruler pan drag
  if (rulerDragging.value) {
    rulerDragging.value = false
    return
  }

  // Handle grid resize end
  if (isResizingGrid.value) {
    store.endGridResize();
    // Restore temp tile after resizing ends
    if (selectedTool.value === SELECTABLE_TOOLS.ADD && selectedShape.value && selectedColor.value) {
      store.setTempItemByCurrentSelection();
    }
    // Save the project with new grid size
    saveProjectDesign();
    return;
  }

  if (!isDrawing.value) return
  isDrawing.value = false
  lastPoint.value = null

  let selectedShapes;

  if (selectedTool.value === SELECTABLE_TOOLS.LASSO) {
    // Handle async lasso selection with progressive updates
    selectedShapes = await handleLassoMouseUp();
  } else {
    // Handle synchronous rect selection
    selectedShapes = handleRectSelectMouseUp();
  }

  if (!selectedShapes) return

  // Ensure final selection is set in store with actual item IDs
  if (selectedShapes.length > 0) {
    const selectedIds = selectedShapes.map((shape) => typeof shape.id === 'function' ? shape.id() : shape.id);
    store.selectItemsOnGrid(selectedIds);
    store.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
  }

  if (selectedTool.value === SELECTABLE_TOOLS.LASSO) { resetLasso() } else { resetRect() }
};

const handleWheel = (e) => {
  if (e.evt.ctrlKey || e.evt.metaKey) {
    e.evt.preventDefault();
    e.evt.cancelBubble = true;

    const stage = stageRef.value.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const zoomFactor = 0.05;
    const newScale =
      e.evt.deltaY < 0
        ? oldScale * (1 + zoomFactor)
        : oldScale * (1 - zoomFactor);
    const newPos = {
      x: pointer.x - (pointer.x - stage.x()) * (newScale / oldScale),
      y: pointer.y - (pointer.y - stage.y()) * (newScale / oldScale),
    };
    store.gridZoom = Math.min(Math.max(newScale, 0.3), 5);
    store.gridPos = getGridPosLimits(newPos);
  } else if (scrollbars.isVisible.value) {
    e.evt.preventDefault();
    const deltaY = e.evt.deltaY;
    const deltaX = e.evt.shiftKey ? e.evt.deltaY : e.evt.deltaX;
    scrollbars.panByDelta(deltaX, deltaY);
  }
};

const handleMobileResizeTouchStart = (e) => {

  // Prevent this touch from interfering with other interactions
  e.cancelBubble = true;
  if (e.evt) {
    e.evt.preventDefault();
    e.evt.stopPropagation();
    e.evt.stopImmediatePropagation();
  }

  // Track this as a mobile handle touch
  isMobileHandleTouched.value = true;
  mobileResizeStartTime.value = performance.now();

  const stage = stageRef.value.getStage();
  const stagePos = stage.position();
  let clientX, clientY;

  // Handle both touch and mouse events
  if (e.evt.touches && e.evt.touches[0]) {
    // Touch event
    clientX = e.evt.touches[0].clientX;
    clientY = e.evt.touches[0].clientY;
  } else if (e.evt.clientX !== undefined) {
    // Mouse event
    clientX = e.evt.clientX;
    clientY = e.evt.clientY;
  } else {
    // Fallback: use stage pointer position
    const pointerPos = stage.getPointerPosition();
    if (pointerPos) {
      clientX = pointerPos.x + stagePos.x;
      clientY = pointerPos.y + stagePos.y;
    } else {
      console.error('Could not determine touch/click position');
      return;
    }
  }

  // Convert position to grid coordinates
  const touchPos = {
    x: (clientX - stagePos.x) / gridZoom.value,
    y: (clientY - stagePos.y) / gridZoom.value
  };

  // Store the start position for movement detection
  mobileResizeStartPos.value = { x: clientX, y: clientY };


  // Determine which handle was touched based on handle configuration
  const rightHandle = rightEdgeMobileHandle.value;
  const bottomHandle = bottomEdgeMobileHandle.value;
  const cornerHandle = cornerMobileHandle.value;

  const touchedEdges = [];

  // Check if touch is within right handle bounds
  if (touchPos.x >= rightHandle.x && touchPos.x <= rightHandle.x + rightHandle.width &&
      touchPos.y >= rightHandle.y && touchPos.y <= rightHandle.y + rightHandle.height) {
    touchedEdges.push('right');
  }

  // Check if touch is within bottom handle bounds
  if (touchPos.x >= bottomHandle.x && touchPos.x <= bottomHandle.x + bottomHandle.width &&
      touchPos.y >= bottomHandle.y && touchPos.y <= bottomHandle.y + bottomHandle.height) {
    touchedEdges.push('bottom');
  }

  // Check if touch is within corner handle bounds
  if (touchPos.x >= cornerHandle.x && touchPos.x <= cornerHandle.x + cornerHandle.width &&
      touchPos.y >= cornerHandle.y && touchPos.y <= cornerHandle.y + cornerHandle.height) {
    touchedEdges.push('right', 'bottom');
  }

  if (touchedEdges.length > 0) {
    store.setHoveredEdges(touchedEdges);
    store.startGridResize(touchPos);
  } else {
  }
};

const handleMobileHandleClick = (e) => {
  e.cancelBubble = true;
  if (e.evt) {
    e.evt.preventDefault();
    e.evt.stopPropagation();
    e.evt.stopImmediatePropagation();
  }
  // Don't do anything else - this is just to prevent tile placement
};

// Helper function to check if a touch position is within any mobile handle area
const isTouchWithinMobileHandle = (touchPos) => {
  const targetSize = touchTargetSize.value;
  const rightEdge = startOffset.value + gridWidth.value;
  const bottomEdge = startOffset.value + gridHeight.value;

  // Right edge handle bounds
  const rightHandle = {
    x: rightEdge - targetSize / 4,
    y: startOffset.value + gridHeight.value / 2 - targetSize / 2,
    width: targetSize / 2,
    height: targetSize
  };

  // Bottom edge handle bounds
  const bottomHandle = {
    x: startOffset.value + gridWidth.value / 2 - targetSize / 2,
    y: bottomEdge - targetSize / 4,
    width: targetSize,
    height: targetSize / 2
  };

  // Corner handle bounds
  const cornerHandle = {
    x: rightEdge - targetSize / 2,
    y: bottomEdge - targetSize / 2,
    width: targetSize / 2,
    height: targetSize / 2
  };

  // Check if touch is within any handle area
  const isInRightHandle = touchPos.x >= rightHandle.x && touchPos.x <= rightHandle.x + rightHandle.width &&
                         touchPos.y >= rightHandle.y && touchPos.y <= rightHandle.y + rightHandle.height;

  const isInBottomHandle = touchPos.x >= bottomHandle.x && touchPos.x <= bottomHandle.x + bottomHandle.width &&
                          touchPos.y >= bottomHandle.y && touchPos.y <= bottomHandle.y + bottomHandle.height;

  const isInCornerHandle = touchPos.x >= cornerHandle.x && touchPos.x <= cornerHandle.x + cornerHandle.width &&
                          touchPos.y >= cornerHandle.y && touchPos.y <= cornerHandle.y + cornerHandle.height;

  return isInRightHandle || isInBottomHandle || isInCornerHandle;
};

// HTML Mobile Handle Touch Event Handlers
const handleMobileHandleTouchStart = (e, edges) => {

  // Prevent default touch behaviors
  e.preventDefault();
  e.stopPropagation();

  // Track this as a mobile handle touch
  isMobileHandleTouched.value = true;
  mobileResizeStartTime.value = performance.now();

  const touch = e.touches[0];
  mobileResizeStartPos.value = { x: touch.clientX, y: touch.clientY };

  // Store which edges are being resized
  store.setHoveredEdges(edges);

};

const handleMobileHandleTouchMove = (e) => {

  if (!isMobileHandleTouched.value) return;

  e.preventDefault();
  e.stopPropagation();

  const touch = e.touches[0];
  const currentPos = { x: touch.clientX, y: touch.clientY };
  const distance = Math.sqrt(
    Math.pow(currentPos.x - mobileResizeStartPos.value.x, 2) +
    Math.pow(currentPos.y - mobileResizeStartPos.value.y, 2)
  );

  // If we've moved more than 10 pixels, start resize mode
  if (distance > 10 && !isResizingGrid.value) {

    const stage = stageRef.value.getStage();
    const stagePos = stage.position();
    const touchPos = {
      x: (mobileResizeStartPos.value.x - stagePos.x) / gridZoom.value,
      y: (mobileResizeStartPos.value.y - stagePos.y) / gridZoom.value
    };

    // Start grid resize (edges were already set in handleMobileHandleTouchStart)
    store.startGridResize(touchPos);
  }

  // Continue with resize if already started
  if (isResizingGrid.value) {
    const stage = stageRef.value.getStage();
    const stagePos = stage.position();
    const touchPos = {
      x: (currentPos.x - stagePos.x) / gridZoom.value,
      y: (currentPos.y - stagePos.y) / gridZoom.value
    };

    handleMobileGridResize(touchPos);
  }
};

const handleMobileHandleTouchEnd = (e) => {

  e.preventDefault();
  e.stopPropagation();

  if (isResizingGrid.value) {
    // End grid resize
    store.endGridResize();

    // Clear hovered edges
    store.setHoveredEdges([]);

    // Save project after resize
    const projectStore = useProjectStore();
    projectStore.save(false, true); // Don't update URL, skip toast
  }

  // Reset mobile handle state after a delay
  setTimeout(() => {
    isMobileHandleTouched.value = false;
  }, 100);
};

const handleTouchStart = (e) => {

  // Store initial touch position for tap vs swipe detection for ALL touches
  if (e.evt.touches && e.evt.touches[0]) {
    const touch = e.evt.touches[0];
    touchStartPosition.value = {
      x: touch.clientX,
      y: touch.clientY
    };
    isTouchMoved.value = false;
    wasMultiTouch.value = false; // Reset multi-touch flag

    // Check if this is multi-touch from the start
    if (e.evt.touches.length > 1) {
      wasMultiTouch.value = true;
    }

    // Check if touch started on the stage (empty area) or on a shape
    // In Konva, e.target is the element that was touched
    // Stage touches will have the stage as the target
    const targetName = e.target.name ? e.target.name() : '';
    const targetId = e.target.id ? e.target.id() : '';

    // If the target has no name and no id, it's likely the stage
    // Shapes will have IDs from our shape data
    touchStartedOnStage.value = !targetId || targetId === '';
  }

  // Check if touch is on a mobile resize handle first
  if (isTouchDevice.value && showMobileResizeHandles.value) {
    const stage = stageRef.value.getStage();
    const stagePos = stage.position();
    const touch = e.evt.touches[0];

    if (touch) {
      // Convert touch coordinates to grid coordinates
      const touchPos = {
        x: (touch.clientX - stagePos.x) / gridZoom.value,
        y: (touch.clientY - stagePos.y) / gridZoom.value
      };

      // Check if touch is within any mobile handle area
      if (isTouchWithinMobileHandle(touchPos)) {
        // Prevent normal touch handling and delegate to mobile handle logic
        handleMobileResizeTouchStart(e, touchPos);
        return;
      }
    }
  }

  // MOBILE FIX: Update mouse event for mobile to ensure temp item movement works
  if (selectedTool.value === SELECTABLE_TOOLS.ADD && e.evt.touches && e.evt.touches[0] && touchStartedOnStage.value && !wasMultiTouch.value) {
    const touch = e.evt.touches[0];

    // Create a synthetic mouse event for the touch
    mouseEvent.value = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      pageX: touch.pageX,
      pageY: touch.pageY
    };

    // Enable animation only if touch started on stage and NOT during multi-touch
    if (!isAnimating.value && !wasMultiTouch.value) {
      isAnimating.value = true;
    }
  }

  isTouching.value = false
}

const handleTouchMove = (e) => {
  // Log less frequently to avoid spam
  const touch1 = e.evt.touches[0]
  const touch2 = e.evt.touches[1]
  const stage = stageRef.value.getStage()

  // Check for multi-touch (pinch-to-zoom, etc)
  if (e.evt.touches.length > 1) {
    wasMultiTouch.value = true;
    // Disable animation immediately when multi-touch is detected
    if (isAnimating.value) {
      isAnimating.value = false;
    }
  }

  // Prevent pull-to-refresh and other browser gestures when interacting with the grid
  if (touch1) {
    // Always prevent default touch behavior within the grid to avoid:
    // - Pull-to-refresh
    // - Overscroll bounce
    // - Accidental page scrolling
    e.evt.preventDefault();
  }

  // Check if touch has moved beyond threshold for tap vs swipe detection
  if (touch1 && !touch2) {
    const currentPos = { x: touch1.clientX, y: touch1.clientY };
    const distance = Math.sqrt(
      Math.pow(currentPos.x - touchStartPosition.value.x, 2) +
      Math.pow(currentPos.y - touchStartPosition.value.y, 2)
    );

    if (distance > SWIPE_THRESHOLD && !isTouchMoved.value) {
      isTouchMoved.value = true;
    }
  }

  // MOBILE FIX: Update mouse event for ADD tool during touch move - only if single touch and not multi-touch
  if (selectedTool.value === SELECTABLE_TOOLS.ADD && touch1 && !touch2 && touchStartedOnStage.value && !wasMultiTouch.value) {
    mouseEvent.value = {
      clientX: touch1.clientX,
      clientY: touch1.clientY,
      pageX: touch1.pageX,
      pageY: touch1.pageY
    };

    // Trigger temp item movement on mobile - only if not multi-touch
    if (isAnimating.value && !wasMultiTouch.value) {
      requestAnimationFrame(moveTempItem);
    }
  }

  // Check if we need to start grid resize based on handle touch and movement
  if (isMobileHandleTouched.value && touch1 && !touch2 && !isResizingGrid.value) {
    const currentPos = { x: touch1.clientX, y: touch1.clientY };
    const distance = Math.sqrt(
      Math.pow(currentPos.x - mobileResizeStartPos.value.x, 2) +
      Math.pow(currentPos.y - mobileResizeStartPos.value.y, 2)
    );

    // If we've moved more than 10 pixels, start resize mode
    if (distance > 10) {
      e.evt.preventDefault();
      e.evt.cancelBubble = true;

      const stagePos = stage.position();
      const touchPos = {
        x: (mobileResizeStartPos.value.x - stagePos.x) / gridZoom.value,
        y: (mobileResizeStartPos.value.y - stagePos.y) / gridZoom.value
      };

      // Determine which handle was touched and start resize
      const rightHandle = rightEdgeMobileHandle.value;
      const bottomHandle = bottomEdgeMobileHandle.value;
      const cornerHandle = cornerMobileHandle.value;

      const touchedEdges = [];

      if (touchPos.x >= rightHandle.x && touchPos.x <= rightHandle.x + rightHandle.width &&
          touchPos.y >= rightHandle.y && touchPos.y <= rightHandle.y + rightHandle.height) {
        touchedEdges.push('right');
      }

      if (touchPos.x >= bottomHandle.x && touchPos.x <= bottomHandle.x + bottomHandle.width &&
          touchPos.y >= bottomHandle.y && touchPos.y <= bottomHandle.y + bottomHandle.height) {
        touchedEdges.push('bottom');
      }

      if (touchPos.x >= cornerHandle.x && touchPos.x <= cornerHandle.x + cornerHandle.width &&
          touchPos.y >= cornerHandle.y && touchPos.y <= cornerHandle.y + cornerHandle.height) {
        touchedEdges.push('right', 'bottom');
      }

      if (touchedEdges.length > 0) {
        store.setHoveredEdges(touchedEdges);
        store.startGridResize(touchPos);
      }
    }
  }

  // Handle grid resize if in progress
  if (isResizingGrid.value && touch1 && !touch2) {
    e.evt.preventDefault();
    e.evt.cancelBubble = true;

    const stagePos = stage.position();
    const touchPos = {
      x: (touch1.clientX - stagePos.x) / gridZoom.value,
      y: (touch1.clientY - stagePos.y) / gridZoom.value
    };

    // Use existing resize logic with touch position
    handleGridResize({ target: { getStage: () => stage }, evt: { clientX: touch1.clientX, clientY: touch1.clientY } });
    return;
  }

  // we need to restore dragging, if it was cancelled by multi-touch
  if (touch1 && !touch2 && !stage.isDragging() && dragStopped.value) {
    stage.startDrag()
    isTouching.value = true
    dragStopped.value = false
  }

  if (touch1 && touch2) {
    isTouching.value = true
    e.evt.cancelBubble = true
    e.evt.preventDefault()
    // if the stage was under Konva's drag&drop
    // we need to stop it, and implement our own pan logic with two pointers
    if (stage.isDragging()) {
      dragStopped.value = true
      stage.stopDrag()
    }

    const p1 = {
      x: touch1.clientX,
      y: touch1.clientY,
    }
    const p2 = {
      x: touch2.clientX,
      y: touch2.clientY,
    }

    if (!lastCenterTouch.value) {
      lastCenterTouch.value = getCenter(p1, p2)
      return
    }
    const newCenter = getCenter(p1, p2)
    const dist = getDistance(p1, p2)

    if (!lastDistTouch.value) {
      lastDistTouch.value = dist
    }

    // local coordinates of center point
    const pointTo = {
      x: (newCenter.x - stage.x()) / stage.scaleX(),
      y: (newCenter.y - stage.y()) / stage.scaleX(),
    }

    const scale = stage.scaleX() * (dist / lastDistTouch.value)

    // calculate new position of the stage
    const dx = newCenter.x - lastCenterTouch.value.x
    const dy = newCenter.y - lastCenterTouch.value.y

    const newPos = {
      x: newCenter.x - pointTo.x * scale + dx,
      y: newCenter.y - pointTo.y * scale + dy,
    }

    store.gridZoom = Math.min(Math.max(scale, 0.3), 5);
    store.gridPos = getGridPosLimits(newPos)
    lastDistTouch.value = dist
    lastCenterTouch.value = newCenter
  }
}

const handleTouchEnd = (e) => {
  const stage = stageRef.value.getStage()

  // Handle grid resize end
  if (isResizingGrid.value) {
    store.endGridResize();
    store.setHoveredEdges([]);

    // Save the updated grid size
    nextTick(() => {
      projectStore.save(false, true);
    });
  }

  // Reset mobile handle tracking
  if (isMobileHandleTouched.value) {
    // If we were touching a handle but didn't drag far enough to start resize,
    // reset after a short delay to prevent tile placement
    const touchDuration = performance.now() - mobileResizeStartTime.value;
    if (touchDuration < 200 && !isResizingGrid.value) {
      // Quick tap on handle - keep blocking tile placement briefly
      setTimeout(() => {
        isMobileHandleTouched.value = false;
      }, 150);
    } else {
      isMobileHandleTouched.value = false;
    }
  }

  if (isResizingGrid.value) return;

  // e.evt.preventDefault()
  // handleDragEnd(e)

  // Always reset touch state on touch end
  lastDistTouch.value = 0
  lastCenterTouch.value = null

  // CRITICAL: Check if there are still active touches
  // This handles the case where fingers are lifted sequentially during pinch
  const stillHasActiveTouches = e.evt.touches && e.evt.touches.length > 0

  // Handle tile placement for ADD tool on touch end - only if single touch gesture

  // Don't place tiles if there are still active touches (sequential finger lift during pinch)
  if (selectedTool.value === SELECTABLE_TOOLS.ADD && !isResizingGrid.value && !isMobileHandleTouched.value && touchStartedOnStage.value && !wasMultiTouch.value && !stillHasActiveTouches) {

    // For drag-to-place: if we moved and have temp items, place the tile
    if (isTouchMoved.value && tempNewItems.value.length > 0) {
      addTempItemsAsGridShape(true)
    }

    // Reset for next interaction
    isTouchMoved.value = false
  }

  // Record when ANY finger is lifted during or after multi-touch
  // This ensures the cooldown period resets with each finger lift
  if (wasMultiTouch.value) {
    multiTouchEndTime.value = Date.now()
  }

  // Only reset wasMultiTouch when all fingers are lifted
  if (!stillHasActiveTouches) {
    wasMultiTouch.value = false
    // Reset touchStartedOnStage when all touches are complete
    // This ensures the next touch interaction starts fresh
    touchStartedOnStage.value = false
  }

  // Only process further if we were in a touching state
  if(isTouching.value) {
    e.evt.cancelBubble = true
    isTouching.value = false
    store.unselectItemsOnGrid()
  }
}

const handleDragStart = (e) => {
  let dragShape = e.target;
  const is3DGroup = e.currentTarget instanceof Konva.Group

  if(is3DGroup) {
    dragShape = e.currentTarget.children.find(child => child.id() === e.currentTarget.id().replace('-group', ''))
    // For 3D groups, use the group's position, not the front face's relative position
    startDragPosition.value = { x: e.currentTarget.x(), y: e.currentTarget.y() };
  } else {
    startDragPosition.value = { x: dragShape.x(), y: dragShape.y() };
  }

  let idsToBeSelected = [dragShape.id()];

  // Set drag in progress flag to prevent clearing initial positions
  isDragInProgress.value = true;

  const found = selectedItemsOnGrid.value.find(
    (item) => item.id === dragShape.id()
  );
  const filteredItems = selectedItemsOnGrid.value.filter(
    (item) => item.id !== dragShape.id()
  );

  if (found || isHoldingSelectTool.value === true) {
    idsToBeSelected = idsToBeSelected.concat(
      ...filteredItems.map((item) => item.id)
    );
  }

  store.selectItemsOnGrid(idsToBeSelected);


  // Initialize positions for all selected items at drag start using current Konva positions
  // Always reset to ensure we have the correct current position (not stale from previous drag)
  const shapesLayer = shapesLayerRef.value.getNode();
  selectedItemsOnGrid.value.forEach(item => {
    // Check if this is a 3D group
    const itemGroup = shapesLayer.findOne(`#${item.id}-group`);
    const is3DItem = itemGroup && itemGroup instanceof Konva.Group;

    if (is3DItem) {
      // For 3D groups, store group position (not group - depth) so that when multiple
      // shapes with different depths are dragged, the same offset preserves relative
      // distances; using (group - depth) would add (leaderDepth - itemDepth) per item
      // and break snap/position for non-leaders.
      const frontFace = itemGroup.children.find(child => child.id() === item.id);
      if (frontFace) {
        initialPositions.value.set(item.id, {
          x: itemGroup.x(),
          y: itemGroup.y(),
          rotation: frontFace.rotation() || 0
        });
      } else {
        // Fallback if front face not found
        initialPositions.value.set(item.id, {
          x: item.x,
          y: item.y,
          rotation: item.rotation || 0
        });
      }
    } else {
      // For regular shapes, use the shape's position
      const konvaShape = shapesLayer.findOne(`#${item.id}`);
      if (konvaShape) {
        initialPositions.value.set(item.id, {
          x: konvaShape.x(),
          y: konvaShape.y(),
          rotation: konvaShape.rotation() || 0
        });
      } else {
        // Fallback to store data if Konva shape not found
        initialPositions.value.set(item.id, {
          x: item.x,
          y: item.y,
          rotation: item.rotation || 0
        });
      }
    }
  });

  store.saveState()

  // Refresh collision shapes and reset drag collision state
  if (gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D) {
    updateDrawnShapesForCollision()
    dragCollision.value = false
  }
};

const specialDragMoveForSpaced = (dragShape) => {
  const dragOffset = {
    x: dragShape.x() - startDragPosition.value.x,
    y: dragShape.y() - startDragPosition.value.y,
  }

  selectedItemsOnGrid.value.forEach((item) => {
    const snap = getSnapGridPosition(
      item.x + dragOffset.x,
      item.y + dragOffset.y,
      getShapeById(item.metadata.shape_id),
    )

    const attrs = {
      x: snap.x,
      y: snap.y,
      rotation: (snap.rotation !== undefined) ? snap.rotation : item.rotation,
    }

    store.updateItemOnList(item.id, attrs)
  })
}

// Single item dragging: store position for drag end update
const dragSingleShape = (dragShape) => {
  const item = selectedItemsOnGrid.value.find(selected => selected.id === dragShape.id())
  const shape = item ? snapShapeOf(item) : getShapeById(dragShape.attrs.metadata.shape_id);
  const snapped = getSnapGridPosition(dragShape.x(), dragShape.y(), shape);

  // Apply visual snapping to Konva shape immediately
  dragShape.position({
    x: snapped.x,
    y: snapped.y
  });

  if (snapped.rotation !== undefined) {
    dragShape.rotation(snapped.rotation);
  }

  // Store for final update at drag end
  pendingUpdates.value.set(dragShape.id(), {
    x: snapped.x,
    y: snapped.y,
    rotation: snapped.rotation !== undefined ? snapped.rotation : dragShape.rotation()
  });

  if (gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D) {
    checkDragCollision();
  }

  return;
}

const dragMultipleShapes = (dragShape) => {
  // Multi-selection dragging: Treat group as "one" item for performance
  // Calculate drag offset from the dragged shape
  const dragOffset = {
    x: dragShape.x() - startDragPosition.value.x,
    y: dragShape.y() - startDragPosition.value.y,
  };

  // Get shapes layer for direct Konva updates
  const shapesLayer = shapesLayerRef.value.getNode();

  // Step 1: Calculate the group's target position using the dragged shape as the leader
  const draggedItem = selectedItemsOnGrid.value.find(item => item.id === dragShape.id().replace('-group', ''));
  if (!draggedItem) {
    return;
  }

  // Step 2: Apply snapping to the dragged shape to determine group movement
  const draggedShape = snapShapeOf(draggedItem);
  const rawLeaderX = startDragPosition.value.x + dragOffset.x;
  const rawLeaderY = startDragPosition.value.y + dragOffset.y;

  let snappedLeaderX = rawLeaderX;
  let snappedLeaderY = rawLeaderY;

  if (draggedShape) {
    const snapped = getSnapGridPosition(rawLeaderX, rawLeaderY, draggedShape);
    snappedLeaderX = snapped.x;
    snappedLeaderY = snapped.y;
  }

  // Step 3: Calculate the unified offset for the entire group
  const groupOffsetX = snappedLeaderX - startDragPosition.value.x;
  const groupOffsetY = snappedLeaderY - startDragPosition.value.y;

  // Step 4: Apply the same offset to ALL selected items (maintaining relative positions)
  selectedItemsOnGrid.value.forEach((item) => {
    const initialPos = initialPositions.value.get(item.id);
    if (!initialPos) {
      return;
    }

    // Apply the unified group offset to maintain relative positions
    const finalX = initialPos.x + groupOffsetX;
    const finalY = initialPos.y + groupOffsetY;
    const finalRotation = initialPos.rotation || 0;

    // Find the Konva shape and move it
    const konvaShape = shapesLayer.findOne(`#${item.id}`);
    if (konvaShape) {
      // Set unified position for true group movement
      konvaShape.position({ x: finalX, y: finalY });
      if (finalRotation !== undefined) {
        konvaShape.rotation(finalRotation);
      }

      // Store for drag end update
      pendingUpdates.value.set(item.id, {
        x: finalX,
        y: finalY,
        rotation: finalRotation
      });
    }
  });

  if (gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D) {
    checkDragCollision();
  }
}

const drag3DShapes = (dragShape) => {
  // Get shapes layer for direct Konva updates
  const shapesLayer = shapesLayerRef.value.getNode();

  // Step 1: Find the dragged group and its front face
  // Handle both group and shape inputs (for compatibility)
  const draggedGroup = dragShape instanceof Konva.Group ? dragShape : shapesLayer.findOne(`#${dragShape.id()}-group`);
  if (!draggedGroup || !(draggedGroup instanceof Konva.Group)) {
    return;
  }

  const draggedId = draggedGroup.id().replace('-group', '');
  const draggedFrontFace = draggedGroup.children.find(child => child.id() === draggedId);
  if (!draggedFrontFace) {
    return;
  }

  // Step 2: Calculate the leader's position using snapping logic
  const depth = draggedFrontFace.attrs.metadata.depth;
  const currentGroupX = draggedGroup.x();
  const currentGroupY = draggedGroup.y();
  const x = currentGroupX - depth;
  const y = currentGroupY - depth;
  const shape = getShapeById(draggedFrontFace.attrs.metadata.shape_id);
  const snapped = getSnapGridPosition(x, y, shape);

  const adjust = depth === 20 ? 10 : 0;
  snapped.x -= adjust;
  snapped.y -= adjust;

  // Step 3: Apply visual snapping to the leader group immediately
  draggedGroup.position({
    x: snapped.x,
    y: snapped.y
  });

  // Store for final update at drag end for the leader
  pendingUpdates.value.set(draggedId, {
    x: snapped.x,
    y: snapped.y,
    rotation: snapped.rotation !== undefined ? snapped.rotation : draggedFrontFace.rotation()
  });

  // Step 4: Handle multiple shapes - apply same group offset to preserve relative positions
  if (selectedItemsOnGrid.value.length > 1) {
    // initialPositions for 3D stores group position (set in handleDragStart)
    const leaderInitialPos = initialPositions.value.get(draggedId);
    if (!leaderInitialPos) {
      update3DGroupZIndices();
      triggerRef(pendingUpdates);
      return;
    }

    // Offset in group position space: same delta for all so distance between shapes is preserved
    const offsetX = snapped.x - leaderInitialPos.x;
    const offsetY = snapped.y - leaderInitialPos.y;

    selectedItemsOnGrid.value.forEach((item) => {
      if (item.id === draggedId) {
        return;
      }

      const initialPos = initialPositions.value.get(item.id);
      if (!initialPos) {
        return;
      }

      const itemGroup = shapesLayer.findOne(`#${item.id}-group`);
      if (!itemGroup || !(itemGroup instanceof Konva.Group)) {
        return;
      }

      const itemFrontFace = itemGroup.children.find(child => child.id() === item.id);
      if (!itemFrontFace) {
        return;
      }

      // New group position = initial group position + same offset (preserves relative distance)
      const newGroupX = initialPos.x + offsetX;
      const newGroupY = initialPos.y + offsetY;

      itemGroup.position({
        x: newGroupX,
        y: newGroupY
      });

      pendingUpdates.value.set(item.id, {
        x: newGroupX,
        y: newGroupY,
        rotation: initialPos.rotation || itemFrontFace.rotation()
      });
    });
  }

  // Step 5: Common cleanup - update z-indices and trigger reactivity
  update3DGroupZIndices();
  triggerRef(pendingUpdates);

  checkDragCollision();
  return;
}

// Track if we have a pending microtask for z-index update
let pendingZIndexMicrotask = false;

// Update z-indices of all 3D groups based on isometric sorting
// Higher (x+y) renders first (behind), lower (x+y) renders last (on top)
// Uses queueMicrotask to run AFTER Konva's drag handling but BEFORE browser render
const update3DGroupZIndices = () => {
  // Skip if we already have a pending microtask
  if (pendingZIndexMicrotask) return;

  pendingZIndexMicrotask = true;

  // queueMicrotask runs after current JS execution but before browser renders
  // This ensures we run after Konva's drag handling but before the visual update
  queueMicrotask(() => {
    pendingZIndexMicrotask = false;
    performZIndexUpdate();
  });
};

// Actual z-index update logic - uses topological sorting for correct z-order
const performZIndexUpdate = () => {
  if (!shapesLayerRef.value) return;

  console.log('[       PERFORM Z INDEX UPDATE (Topological)      ]')

  const layer = shapesLayerRef.value.getNode();
  if (!layer) return;

  // Get all 3D groups from the layer
  const groups = layer.find('Group').filter(g => g.id()?.endsWith('-group'));
  if (groups.length === 0) return;

  // Build shapes with effective center positions for sorting
  const shapesForSort = groups.map(group => {
    const shapeId = group.id().replace('-group', '');
    const shape = shapeList.value.find(s => s.id === shapeId);
    if (!shape) return null;

    const groupX = group.x();
    const groupY = group.y();

    return {
      ...shape,
      // Store effective center for sorting (shape center + group offset during drag)
      x: (shape.x ?? 0) + groupX,
      y: (shape.y ?? 0) + groupY,
      _group: group // Keep reference to Konva group
    };
  }).filter(Boolean);

  // Sort using topological sort
  const sortedShapes = sort3dShapeList(shapesForSort, null);

  // Apply z-order by calling moveToTop() in sorted order
  // Each call puts the group on top, so the LAST one ends up visually on top
  sortedShapes.forEach(shape => {
    shape._group.moveToTop();
  });

  // Force immediate redraw to apply z-order changes
  layer.batchDraw();
};

// Function to update drawnShapesForCollision from Konva layer
const updateDrawnShapesForCollision = () => {
    if (!shapesLayerRef.value) {
      drawnShapesForCollision.value = []
      return
    }

    const layer = shapesLayerRef.value.getNode()
    if (!layer) {
      drawnShapesForCollision.value = []
      return
    }

    // In 3D preview mode, shapes are in groups - extract front faces (non-ignoreCollision children)
    // In 2D mode, shapes are direct children of the layer
    if (showDepthPreview.value) {
      // Get all groups (3D shape groups)
      const groups = layer.children.filter((node) => node instanceof Konva.Group)
      // Extract front faces from groups (depth faces have ignoreCollision: true)
      const frontFaces = groups
        .flatMap((group) => group.children)
        .filter((node) => node.attrs.ignoreCollision !== true)
      drawnShapesForCollision.value = frontFaces
    } else {
      // 2D mode: shapes are direct children or in groups (for other grouping scenarios)
      const drawnShapes = layer.children.filter((node) => !(node instanceof Konva.Group))
      const drawnShapesInGroups = layer.children
        .filter((node) => node instanceof Konva.Group)
        .flatMap((node) => node.children)
        .filter((node) => node.attrs.ignoreCollision !== true)
      drawnShapesForCollision.value = drawnShapes.concat(drawnShapesInGroups)
    }
}

const handleDragMove = (e) => {
  let dragShape = e.target;
  const is3DGroup = e.currentTarget instanceof Konva.Group

  // Only recalculate if we have selected items
  if (selectedItemsOnGrid.value.length === 0) {
    return;
  }

  if(is3DGroup) {
    return drag3DShapes(e.currentTarget)
  }

  if (dragShape.attrs.metadata.shape_id === 'SmallDiamondSpaced') {
    return specialDragMoveForSpaced(dragShape)
  }

  if (selectedItemsOnGrid.value.length === 1) {
    return dragSingleShape(dragShape)
  }

  if (selectedItemsOnGrid.value.length > 1) {
    return dragMultipleShapes(dragShape)
  }
};

const handleDragEnd = (e) => {
  e.evt.cancelBubble = true
  e.evt.preventDefault()

  // Clear drag in progress flag
  isDragInProgress.value = false;

  // Cancel any pending throttled updates
  if (dragThrottleId.value) {
    cancelAnimationFrame(dragThrottleId.value);
    dragThrottleId.value = null;
  }

  const shapesLayer = shapesLayerRef.value.getNode();

  // If collision detected, revert all dragged shapes to their initial positions
  if (dragCollision.value && gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D) {
    selectedItemsOnGrid.value.forEach(item => {
      const initialPos = initialPositions.value.get(item.id)
      if (!initialPos) return

      if (showDepthPreview.value) {
        const group = shapesLayer.findOne(`#${item.id}-group`)
        if (group && group instanceof Konva.Group) {
          group.position({ x: initialPos.x, y: initialPos.y })
          // Reset tint on all faces, restoring original opacities (preserves black overlay faces)
          group.children.forEach(face => {
            face.opacity(face._originalOpacity ?? 1)
            delete face._originalOpacity
          })
          const frontFace = group.children.find(f => f.id() === item.id)
          if (frontFace) frontFace.fill(frontFace.attrs.metadata?.color || null)
        }
      } else {
        const node = shapesLayer.findOne(`#${item.id}`)
        if (node) {
          node.position({ x: initialPos.x, y: initialPos.y })
          node.opacity(1)
          node.fill(node.attrs.metadata?.color || null)
        }
      }
    })

    if (showDepthPreview.value) {
      update3DGroupZIndices()
    }

    pendingUpdates.value.clear()
    initialPositions.value.clear()
    dragCollision.value = false
    isDragInProgress.value = false
    return
  }

  // Reset drag collision and any visual tint for successful drops
  if (gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D) {
    dragCollision.value = false
    selectedItemsOnGrid.value.forEach(item => {
      if (showDepthPreview.value) {
        const group = shapesLayer.findOne(`#${item.id}-group`)
        if (group && group instanceof Konva.Group) {
          group.children.forEach(face => {
            face.opacity(face._originalOpacity ?? 1)
            delete face._originalOpacity
          })
          const frontFace = group.children.find(f => f.id() === item.id)
          if (frontFace) frontFace.fill(frontFace.attrs.metadata?.color || null)
        }
      } else {
        const node = shapesLayer.findOne(`#${item.id}`)
        if (node) {
          node.opacity(1)
          node.fill(node.attrs.metadata?.color || null)
        }
      }
    })
  }

  // Single consolidated update mechanism - read final positions from Konva shapes
  const finalUpdates = [];

  selectedItemsOnGrid.value.forEach(item => {
    const konvaShape = shapesLayer.findOne(`#${item.id}`);
    const konvaGroup = shapesLayer.findOne(`#${item.id}-group`);

    if (!konvaShape) return

    let finalPosition = {
      x: konvaShape.x(),
      y: konvaShape.y(),
      rotation: konvaShape.rotation()
    };

    if (konvaGroup) {
      finalPosition = {
        x: konvaShape.x() + konvaGroup.x() - konvaGroup.attrs.depth,
        y: konvaShape.y() + konvaGroup.y() - konvaGroup.attrs.depth,
        rotation: konvaShape.rotation()
      };
      konvaGroup.position({ x: 0, y: 0 })
    }

    finalUpdates.push({
      id: item.id,
      attrs: finalPosition
    });
  });

  // Single batch update with all selected items' final positions
  if (finalUpdates.length > 0) {
    // Auto-grow the grid if any dragged shape now extends past the current
    // bounds (e.g. user dragged to the right edge). This mirrors the
    // behavior of the new-shape drop path so existing-shape drag is
    // consistent. Skipped for 3D mode (handled by the 3D-revert branch
    // above).
    if (gridPattern.value?.id !== GRID_PATTERN_IDS.THREE_D) {
      const draggedCells = finalUpdates.map((u) => {
        const item = selectedItemsOnGrid.value.find((s) => s.id === u.id)
        if (!item) return null
        const isTurned = Math.abs((u.attrs.rotation || 0) % 180) === 90
        const w = isTurned ? (item.height || item.h || 0) : (item.width || item.w || 0)
        const h = isTurned ? (item.width || item.w || 0) : (item.height || item.h || 0)
        return { x: u.attrs.x, y: u.attrs.y, width: w, height: h, rotation: u.attrs.rotation || 0 }
      }).filter(Boolean)
      // draggedCells are in absolute canvas coords (from Konva). The
      // store action expects grid-relative coords, so we strip
      // startOffset here.
      const draggedCellsRel = draggedCells.map((c) => ({
        x: c.x - startOffset.value,
        y: c.y - startOffset.value,
        width: c.width,
        height: c.height,
        rotation: c.rotation,
      }))
      const cursorAbs = mouseEvent.value
        ? { x: mouseEvent.value.x / gridZoom.value, y: mouseEvent.value.y / gridZoom.value }
        : { x: 0, y: 0 }
      const grow = store.autoExpandGridForPlacement(draggedCellsRel, cursorAbs)
      if (grow.capped) {
        Toast({ icon: 'warning', title: 'Grid is full', text: 'Maximum grid size reached.', customClass: { popup: 'swal-toast-vertical' } })
        // Revert to initial positions so the user can try a different spot.
        selectedItemsOnGrid.value.forEach((item) => {
          const initialPos = initialPositions.value.get(item.id)
          if (!initialPos) return
          const node = shapesLayer.findOne(`#${item.id}`)
          if (node) node.position({ x: initialPos.x, y: initialPos.y })
        })
        initialPositions.value.clear()
        pendingUpdates.value.clear()
        return
      }
    }
    store.updateBatchOnList(finalUpdates);
    store.expandGridToFitShapes()
  }

  // Re-apply 3D z-order from new positions (otherwise layer order is wrong after drag end)
  if (showDepthPreview.value) {
    update3DGroupZIndices();
  }

  // Clear initial positions and pending updates for next drag operation
  initialPositions.value.clear();
  pendingUpdates.value.clear();

  saveProjectDesign()
}

const handleShapeMouseEnter = () => {
  isAnimating.value = false;
};

const handleShapeMouseLeave = () => {
  isAnimating.value = true;
};

const handleArrowMove = (e) => {
  const allowedTools = [
    SELECTABLE_TOOLS.SELECT,
    SELECTABLE_TOOLS.ADD,
    SELECTABLE_TOOLS.LASSO,
  ]

  if (!allowedTools.includes(selectedTool.value)) return
  if (selectedItemsOnGrid.value.length < 1) return;

  const step = store.arrowNudgeStep(e.shiftKey)
  const newPosition = (item, key) => {
    switch (key) {
      case 'ArrowRight':
        return { x: item.x + step.x }
      case 'ArrowLeft':
        return { x: item.x - step.x }
      case 'ArrowUp':
        return { y: item.y - step.y }
      case 'ArrowDown':
        return { y: item.y + step.y }
    }
  };

  // In 3D mode, check if arrow move would cause a collision before applying
  if (gridPattern.value?.id === GRID_PATTERN_IDS.THREE_D) {
    const movingIds = new Set(selectedItemsOnGrid.value.map(item => item.id))
    const isDepth = showDepthPreview.value
    const shapesLayer = shapesLayerRef.value?.getNode()
    const zoom = gridZoom.value
    if (!shapesLayer) return

    for (const item of selectedItemsOnGrid.value) {
      const pos = newPosition(item, e.key)
      const dx = (pos.x ?? item.x) - item.x
      const dy = (pos.y ?? item.y) - item.y

      // Find the Konva node to get accurate shape points
      let node
      if (isDepth) {
        const group = shapesLayer.findOne(`#${item.id}-group`)
        if (group && group instanceof Konva.Group) {
          node = group.children.find(child => child.id() === item.id)
        }
      } else {
        node = shapesLayer.findOne(`#${item.id}`)
      }
      if (!node) continue

      // Get shape points in layer space (relativeTo: shapesLayer)
      let shape1Points = getShapePoints(node, zoom, shapesLayer)
      // In depth mode, points are front-face in layer space; subtract depth (layer units) to get footprint
      if (isDepth) {
        const depth1 = node.attrs.metadata?.depth || 0
        shape1Points = offsetShapePoints(shape1Points, depth1, depth1)
      }
      // Shift points to the would-be position
      shape1Points = {
        ...shape1Points,
        points: shape1Points.points.map(p => ({ x: p.x + dx, y: p.y + dy }))
      }

      for (const collisionShape of drawnShapesForCollision.value) {
        const collisionId = collisionShape.id()
        if (movingIds.has(collisionId)) continue

        let shape2Points = getShapePoints(collisionShape, zoom, shapesLayer)
        if (isDepth) {
          const depth2 = collisionShape.attrs.metadata?.depth || 0
          shape2Points = offsetShapePoints(shape2Points, depth2, depth2)
        }

        if (checkShapeIntersection(shape1Points, shape2Points)) {
          return // Block the move
        }
      }
    }
  }


  store.saveState();
  store.selectItemsOnGrid(
    selectedItemsOnGrid.value.map((item) => {
      store.updateItemOnList(item.id, newPosition(item, e.key));
      return item.id;
    })
  );
  saveProjectDesign();
};

const handleTempGroupClick = (e) => {
  e.cancelBubble = true;

  // Don't place tiles if we're in resize mode
  if (isResizingGrid.value) {
    return
  }

  // it only works for non-touch devices since collision detection is currently on mouse move
  const hasCollision = e.currentTarget.children.some((item) => {
    if (item instanceof Konva.Group) {
      return item.children?.some(face => face.attrs.collision === true)
    }
    return item.attrs.collision === true
  })
  if (hasCollision) {
    Toast({ icon: 'warning', title: 'Tiles cannot overlap', text: 'Please place the tile in an empty spot', customClass: { popup: 'swal-toast-vertical' } })
    return;
  }

  addTempItemsAsGridShape()
};

const handleTap = (e) => {
  if (isTouching.value) return
  if (e.evt.touches.length > 1 || e.evt.changedTouches.length > 1) return
  e.target.moveToTop()
  e.cancelBubble = true
  store.updateSelectedTool(SELECTABLE_TOOLS.ADD)

  if (!keepSelection.value) {
    store.selectItemsOnGrid([e.target.id()])
    keepSelection.value = true
    return
  }

  const allSelected = selectedItemsOnGrid.value.map((item) => item.id)
  const selectionFiltered = allSelected.filter((id) => id !== e.target.id())

  if (allSelected.length === selectionFiltered.length) selectionFiltered.push(e.target.id())

  store.selectItemsOnGrid(selectionFiltered)
}

const handleSelect = (e) => {
  const allowedTools = [
    SELECTABLE_TOOLS.SELECT,
    SELECTABLE_TOOLS.ADD,
    SELECTABLE_TOOLS.LASSO,
    SELECTABLE_TOOLS.RECT_SELECT,
    SELECTABLE_TOOLS.CUT,
  ]

  if (!allowedTools.includes(selectedTool.value)) return

  let target = e.target

  if (e.currentTarget instanceof Konva.Group) {
    target = e.currentTarget.children.find(child => child.id() === e.currentTarget.id().replace('-group', ''))
  } else {
    target.moveToTop();
  }

  e.cancelBubble = true;

  // For CUT tool, keep the tool active and just select the tile
  if (selectedTool.value !== SELECTABLE_TOOLS.CUT) {
    store.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
  }

  if (isHoldingSelectTool.value) {
    const allSelected = selectedItemsOnGrid.value.map((item) => item.id)
    const selectionFiltered = allSelected.filter((id) => id !== target.id())
    if (allSelected.length === selectionFiltered.length) selectionFiltered.push(target.id())
    store.selectItemsOnGrid(selectionFiltered)
  } else {
    store.selectItemsOnGrid([target.id()])
  }
}

// Freeform and 3D turns land exactly on their lattice. Everything else goes through the grid's own snap after a turn.
const turnNeedsSnap = computed(() => gridPattern.value?.id !== GRID_PATTERN_IDS.FREEFORM && gridPattern.value?.id !== GRID_PATTERN_IDS.THREE_D)
const snapTurnedTile = (point, tile, next) => {
  const def = getShapeById(next.shapeId)
  const isSlat = isWoodSlatShapeId(def.id)
  // On Square only slats leave the lattice, since their corner snaps to a coarser step. The generic Square snap would move small shapes.
  if (gridPattern.value.id === GRID_PATTERN_IDS.SQUARE && !isSlat) return point
  const turned = Math.abs(next.rotation % 180) === 90
  const { x, y } = getSnapGridPosition(point.x, point.y, { ...def, rotation: next.rotation, ...(isSlat && turned ? { w: def.h, h: def.w } : {}) })
  return { x, y }
}

// Pivot of the last block turn, so clicking again on the untouched selection keeps turning about the same point.
let lastBlockRotation = null
const selectionSignature = (list) => list.map(t => `${t.id}:${t.x}:${t.y}:${t.rotation}`).join('|')

const rotateSelectionAsBlock = async () => {
  const selected = [...selectedItemsOnGrid.value]
  const tiles = selected.map((item) => {
    const shapeId = item.metadata?.shape_id ?? item.attrs?.metadata?.shape_id
    const def = getShapeById(shapeId)
    return { id: item.id, x: item.x, y: item.y, shapeId, rotation: item.rotation ?? item.attrs?.rotation ?? 0,
      width: item.width ?? def?.w ?? 120, height: item.height ?? def?.h ?? 120 }
  })
  const others = store.shapeList.filter(s => !s.ignoreCollision)
  const samePivot = lastBlockRotation?.signature === selectionSignature(tiles) ? lastBlockRotation.anchor : undefined
  const result = rotateSelectionClockwise(tiles, placementSnap.value?.x || 30, others, {
    pivot: samePivot,
    angle: blockTurnAngle.value,
    snapY: placementSnap.value?.y,
    origin: startOffset.value,
    snapPoint: turnNeedsSnap.value ? snapTurnedTile : undefined,
    detectOverlap: gridPattern.value?.collisionLevel !== CollisionLevel.NONE,
  })
  lastBlockRotation = { anchor: result.anchor, signature: selectionSignature(result.moves.map(m => ({ ...m, rotation: m.nextRotation }))) }

  store.isProcessing = true
  try {
    const turned = await Promise.all(result.moves.map(async (move) => {
      const item = selected.find(s => s.id === move.id)
      const pos = { x: move.x, y: move.y }
      // A slat turns by rotation alone, so it needs no rebuild.
      if (!move.nextShapeId || move.nextShapeId === (item.metadata ?? item.attrs?.metadata)?.shape_id) {
        return { ...item, ...pos, rotation: move.nextRotation, attrs: { ...item.attrs, ...pos, rotation: move.nextRotation } }
      }
      const metadata = { ...item.metadata, ...item.attrs?.metadata, shape_id: move.nextShapeId }
      return transformShapeForAdd({ ...item, ...pos, rotation: move.nextRotation, metadata,
        attrs: { ...item.attrs, ...pos, rotation: move.nextRotation, metadata } })
    }))
    store.saveState()
    for (const item of turned) store.shapeList[store.shapeList.findIndex(s => s.id === item.id)] = item
    store.selectItemsOnGrid(selected.map(item => item.id))
    store.expandGridToFitShapes()
    store.updateShapeList3D()
    if (result.collidingIds.length) {
      Toast({ icon: 'warning', title: 'Tiles overlap', text: 'Some tiles overlap after rotating.', customClass: { popup: 'swal-toast-vertical' } })
    }
    saveProjectDesign()
  } catch (error) {
    console.error('Error rotating selection:', error)
  } finally {
    store.isProcessing = false
  }
}

const handleRotateClick = async (e) => {
  e.cancelBubble = true
  e.evt?.stopPropagation?.()

  if (store.isProcessing) return
  if (selectedItemsOnGrid.value.length > 1) return rotateSelectionAsBlock()
  if (selectedItemsOnGrid.value.length !== 1) return

  const item = selectedItemsOnGrid.value[0]
  const nextRotation = getNextRotation(item.metadata.shape_id)

  if (!nextRotation) return

  // Find a valid position: in place, or jump to closest non-colliding nearby position
  const snap = placementSnap.value?.x || 30
  const validPos = findNearbyNonCollidingPosition(item, nextRotation.id, nextRotation.rotation, store.shapeList, snap, 12, gridPattern.value?.id)

  if (!validPos) {
    Toast({
      icon: 'warning',
      title: 'Cannot rotate tile',
      text: 'No space to rotate tile nearby',
      customClass: { popup: 'swal-toast-vertical' },
    })
    return
  }

  // Save state for undo
  store.saveState()

  // Create a temporary shape object with the new shape_id and position
  const updatedMetadata = {
    ...item.metadata,
    ...(item.attrs?.metadata || {}),
    shape_id: nextRotation.id,
  }
  const tempShapeForTransform = {
    ...item,
    x: validPos.x,
    y: validPos.y,
    metadata: updatedMetadata,
    attrs: {
      ...item.attrs,
      x: validPos.x,
      y: validPos.y,
      metadata: updatedMetadata,
      rotation: nextRotation.rotation,
    },
    rotation: nextRotation.rotation,
  }

  try {
    // Transform the shape to regenerate all properties (width, height, type, fillPatternImage, etc.)
    // This ensures the entire shape is properly replaced, not just metadata
    const transformedShape = await transformShapeForAdd(tempShapeForTransform)

    // Find the item in the shapeList and replace it entirely
    const shapeIndex = store.shapeList.findIndex(s => s.id === item.id)
    if (shapeIndex !== -1) {
      // Preserve selection styling if the item was selected
      const wasSelected = store.selectedItemsOnGrid.some(selected => selected.id === item.id)

      // Replace the entire shape object with the transformed one
      store.shapeList[shapeIndex] = transformedShape

      // Re-apply selection styling if the item was selected
      if (wasSelected) {
        transformedShape.dash = undefined
        transformedShape.dashOffset = undefined
        transformedShape.strokeWidth = 4
        transformedShape.stroke = '#313558'
      }

      // Update selectedItemsOnGrid to reference the new shape object
      const selectedItemIndex = store.selectedItemsOnGrid.findIndex(selected => selected.id === item.id)
      if (selectedItemIndex !== -1) {
        store.selectedItemsOnGrid[selectedItemIndex] = transformedShape
      }

      // Move item to top to ensure it renders correctly
      store.moveItemToTop(item.id)

      if (validPos.x !== item.x || validPos.y !== item.y) {
        store.expandGridToFitShapes()
      }

      if (store.gridPattern?.id === GRID_PATTERN_IDS.THREE_D) {
        store.updateShapeList3D()
      }
    }

    saveProjectDesign()
  } catch (error) {
    console.error('Error rotating shape:', error)
    // Fallback to the previous method if transformation fails
    store.updateItemOnList(item.id, {
      x: validPos.x,
      y: validPos.y,
      rotation: nextRotation.rotation,
      metadata: {
        ...item.metadata,
        shape_id: nextRotation.id,
      },
    })
    saveProjectDesign()
  }
}

const handleDelete = (e) => {
  const allowedTools = [
    SELECTABLE_TOOLS.SELECT,
    SELECTABLE_TOOLS.ADD,
    SELECTABLE_TOOLS.LASSO,
    SELECTABLE_TOOLS.RECT_SELECT,
  ]

  if (!allowedTools.includes(selectedTool.value)) return

  store.saveState();
  store.deleteSelectedItemsOnGrid();
  e?.preventDefault();

  // Switch back to ADD tool after deletion for better mobile UX
  store.updateSelectedTool(SELECTABLE_TOOLS.ADD);
};

// Sync the Konva stage's pixel size to the container without touching the
// user's pan/zoom. Used when the container itself grows or shrinks while the
// browser window is unchanged — e.g. collapsing the mobile bottom sheet,
// which reclaims 288px for #mainContent via flex-1. Before this, the stage
// kept its old height and the newly available area rendered blank grey.
//
// The update is DEBOUNCED to the end of the resize. The mobile sheet's
// collapse animates its height over 300ms, and the container resizes on
// every one of those frames. Each resize makes Konva reallocate the canvas
// backing store and redraw every layer — 34 reallocations for one collapse,
// which is what made the sheet animation visibly heavier than the color
// drawer's (a pure transform, no reallocation). Deferring to the end keeps
// the animation on the compositor and pays for one reallocation instead.
const STAGE_RESIZE_DEBOUNCE_MS = 120;

let stageResizeTimer = null;

const applyStageSize = () => {
  if (!stageContainer.value) return;
  const w = stageContainer.value.offsetWidth;
  const h = stageContainer.value.offsetHeight;
  if (stageConfig.width === w && stageConfig.height === h + stageOverflow()) return;
  stageConfig.width = w;
  stageConfig.height = h + stageOverflow();
};

const handleStageResize = () => {
  if (stageResizeTimer !== null) clearTimeout(stageResizeTimer);
  stageResizeTimer = setTimeout(() => {
    stageResizeTimer = null;
    applyStageSize();
  }, STAGE_RESIZE_DEBOUNCE_MS);
};

let stageResizeObserver = null;

// Only runs on an actual window resize, where re-fitting the zoom is the
// intended behavior (rotation, keyboard open/close, etc.).
const handleResize = () => {
  stageConfig.width = stageContainer.value.offsetWidth;
  stageConfig.height = stageContainer.value.offsetHeight + stageOverflow();

  // On mobile, recalculate zoom when viewport changes (rotation, keyboard, etc.)
  if (isMobile.value) {
    const newZoom = calculateMobileZoom(
      gridWidth.value,
      gridHeight.value,
      stageConfig.width,
      visibleStageSize.value.height,
      7
    );

    // Only update if zoom changed significantly (avoid jitter)
    if (Math.abs(store.gridZoom - newZoom) > 0.05) {
      console.log('[CanvasGrid] Viewport resized, adjusting zoom:', {
        oldZoom: store.gridZoom,
        newZoom,
        viewport: `${stageConfig.width}px × ${stageConfig.height}px`
      });

      store.gridZoom = newZoom;

      // Recenter the grid
      const centerPos = calculateMobileCenterPosition(
        gridWidth.value,
        gridHeight.value,
        stageConfig.width,
        visibleStageSize.value.height,
        newZoom,
        startOffset.value
      );

      store.gridPos = centerPos;
    }
  }
};

const isKeyEventAllowed = (e) => {
  if (modalStore.isAnyVisible) return false;
  if (e.target.className.includes("select-text")) return false;
  if (e.target.className.includes("select-all")) return false;
  if (e.target.tagName.toLowerCase() === 'input') return false;

  // For zoom shortcuts (Ctrl/Cmd + Plus/Minus), only handle when canvas is focused
  const isZoomShortcut = (e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=' || e.key === '-');
  if (isZoomShortcut) {
    const isCanvasFocused = document.activeElement === stageContainer.value;
    if (!isCanvasFocused) {
      return false;
    }
  }

  return true
}

const handleKeyDown = (e) => {
  if (!isKeyEventAllowed(e)) return

  if (e.ctrlKey || e.metaKey) {
    switch (e.key) {
      case "z":
        store.undo()
        e.preventDefault()
        break
      case "y":
        store.redo()
        e.preventDefault()
        break
      case "a":
        store.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
        store.selectAllItemsOnGrid()
        e.cancelBubble = true
        e.preventDefault()
        break
      case "c":
        store.updateSelectedTool(SELECTABLE_TOOLS.COPY)
        store.copySelectedItems()
        break
      case "x":
        store.updateSelectedTool(SELECTABLE_TOOLS.CUT)
        store.cutSelectedItems()
        break
      case "v":
        if (!tempGroupRef.value) return
        tempGroupRef.value.getNode().fire("click")
        break
      case "+":
      case "=":
        store.gridZoom = Math.min(Math.max(store.gridZoom + 0.1, 0.3), 5)
        e.preventDefault()
        break
      case "-":
        store.gridZoom = Math.min(Math.max(store.gridZoom - 0.1, 0.3), 5)
        e.preventDefault()
        break
      case "ArrowUp":
        store.moveItemsToFront(selectedItemsOnGrid.value.map((item) => item.id))
        e.preventDefault()
        break
      case "ArrowDown":
        store.moveItemsToBack(selectedItemsOnGrid.value.map((item) => item.id))
        e.preventDefault()
        break
    }
  }
  else {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowLeft":
      case "ArrowUp":
      case "ArrowDown":
        handleArrowMove(e)
        break
      case "Delete":
      case "Backspace":
        handleDelete(e)
        break
      case "v":
        store.updateSelectedTool(SELECTABLE_TOOLS.ADD)
        break
      case "l":
        if (runtimeConfig.public.lassoSelectActive) { store.updateSelectedTool(SELECTABLE_TOOLS.LASSO) }
        else if (runtimeConfig.public.rectSelectActive) { store.updateSelectedTool(SELECTABLE_TOOLS.RECT_SELECT) }
        break
      case "Escape":
        store.selectItemsOnGrid([])
        store.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
        break
    }

    if (e.code === "Space" && selectedTool.value !== SELECTABLE_TOOLS.MOVE_GRID) {
      prevSelectedTool.value = selectedTool.value
      store.updateSelectedTool(SELECTABLE_TOOLS.MOVE_GRID)
    }

    if (e.key === "Shift") {
      isHoldingSelectTool.value = true;
    }
  }
}

const handleKeyUp = (e) => {
  if (!isKeyEventAllowed(e)) return

  if (e.code === "Space") {
    store.updateSelectedTool(prevSelectedTool.value);
    prevSelectedTool.value = null;
  }

  if (e.key === "Shift") {
    isHoldingSelectTool.value = false;
  }
};

const handleStageClick = () => {
  if ([SELECTABLE_TOOLS.COPY, SELECTABLE_TOOLS.CUT].includes(selectedTool.value)) {
    tempGroupRef.value?.getNode().fire("click");
    return;
  }

  const nextTool = resolveBlankCanvasClickTool({
    returnToAddOnBlankClick: props.returnToAddOnBlankClick,
    selectedTool: selectedTool.value,
    selectedCount: selectedItemsOnGrid.value.length,
  })

  store.selectItemsOnGrid([]);
  if (nextTool !== selectedTool.value) store.updateSelectedTool(nextTool)
};

const handleStageTap = (e) => {
  const timeSinceMultiTouch = Date.now() - multiTouchEndTime.value
  e.evt.cancelBubble = true
  e.evt.preventDefault()

  // Check for multi-touch - don't process tap if multiple fingers, was multi-touch, or within cooldown period
  if ((e.evt.touches && e.evt.touches.length > 1) || wasMultiTouch.value || timeSinceMultiTouch < MULTI_TOUCH_COOLDOWN) {
    // Don't reset wasMultiTouch here - let handleTouchEnd manage it properly
    return
  }

  // Don't place tiles if we're in resize mode or if a mobile handle was recently touched
  if (isResizingGrid.value || isMobileHandleTouched.value) {
    // Reset the handle touched flag after a short delay
    setTimeout(() => {
      isMobileHandleTouched.value = false;
    }, 100);
    return
  }

  // For ADD tool, check if we should place a tile - only if touch started on stage
  if ([SELECTABLE_TOOLS.ADD, SELECTABLE_TOOLS.MOVE_GRID].includes(selectedTool.value) && touchStartedOnStage.value) {

    // Only handle tap (no movement) here since drag-to-place is handled in touchend
    if (!isTouchMoved.value) {
      isTouching.value = false
      addTempItemsAsGridShape(true)
      return
    } else {
      isTouchMoved.value = false
      return
    }
  }

  // For other tools or if no tile should be placed
  isTouchMoved.value = false
  isTouching.value = false
}

function handleStageDrag (pos) {
  return getGridPosLimits(pos)
}

const saveProjectDesign = () => {
  if (runtimeConfig.public.autoSaveOnChange === true) {
    projectStore.save(true, true);
  }
};

// Function to validate and correct item positions to ensure they are at valid snap positions
const validateItemPositions = () => {
  if (gridPattern.value.id !== GRID_PATTERN_IDS.SQUARE_45) return
  console.log('[       VALIDATE ITEM POSITIONS      ]')

  // Only validate if we have items and valid positions are calculated
  if (shapeList.value.length === 0 || !valid45degPositions.value.length) return

  let correctedCount = 0
  const itemsToUpdate = []

  shapeList.value.forEach(item => {
    const shape = getShapeById(item.metadata.shape_id)
    if (!shape) return

    const snapped = getSnapGridPosition(item.x, item.y, shape)
    const distance = Math.sqrt(Math.pow(item.x - snapped.x, 2) + Math.pow(item.y - snapped.y, 2))

    // If the item is more than 1 pixel away from a valid snap position, mark it for correction
    if (distance > 1) {
      itemsToUpdate.push({
        id: item.id,
        x: snapped.x,
        y: snapped.y,
        rotation: snapped.rotation !== undefined ? snapped.rotation : item.rotation
      })
    }
  })

  // Batch update all corrections at once
  if (itemsToUpdate.length > 0) {
    itemsToUpdate.forEach(update => {
      store.updateItemOnList(update.id, update)
    })
    correctedCount = itemsToUpdate.length
  }

  if (correctedCount > 0 && import.meta.dev) {
  }
}

onMounted(() => {
  window.addEventListener("resize", handleResize);
  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);

  // Observe the container's own size, not just the window's. The mobile
  // bottom sheet collapses by shrinking a flex sibling, which changes this
  // container without firing window.resize — that's what left the grid area
  // blank while the canvas kept its old height.
  if (stageContainer.value && typeof ResizeObserver !== 'undefined') {
    stageResizeObserver = new ResizeObserver(() => handleStageResize());
    stageResizeObserver.observe(stageContainer.value);
  }
  // Initialize 3D shapes if depth preview is already enabled
  if (showDepthPreview.value) {
    store.updateShapeList3D()
  }
  handleResize();

  projectStore.setGridRef(stageRef.value.getNode());

  // Set initial zoom for mobile devices to ensure 7ft × 7ft is visible
  console.log('[CanvasGrid] Mobile zoom check:', {
    isMobile: isMobile.value,
    currentZoom: store.gridZoom,
    windowWidth: process.client ? window.innerWidth : 'N/A'
  });

  if (isMobile.value && store.gridZoom === 1) {
    // Calculate optimal zoom to show 7ft × 7ft area
    const viewportWidth = stageConfig.width;
    const viewportHeight = visibleStageSize.value.height;

    const optimalZoom = calculateMobileZoom(
      gridWidth.value,
      gridHeight.value,
      viewportWidth,
      viewportHeight,
      7 // Target 7ft × 7ft visible area
    );

    console.log('[CanvasGrid] Setting mobile zoom:', {
      calculated: optimalZoom,
      gridSize: `${gridWidth.value}px × ${gridHeight.value}px`,
      gridFeet: `${(gridWidth.value / 120).toFixed(1)}ft × ${(gridHeight.value / 120).toFixed(1)}ft`,
      viewport: `${viewportWidth}px × ${viewportHeight}px`,
      targetArea: '7ft × 7ft (840px × 840px)'
    });

    store.gridZoom = optimalZoom;

    // Center the grid in the viewport
    const centerPos = calculateMobileCenterPosition(
      gridWidth.value,
      gridHeight.value,
      viewportWidth,
      viewportHeight,
      optimalZoom,
      startOffset.value
    );

    store.gridPos = centerPos;

    console.log('[CanvasGrid] Grid centered at:', centerPos);
  } else {
    console.log('[CanvasGrid] Skipping mobile zoom:', {
      reason: !isMobile.value ? 'Not mobile' : 'Zoom already set to ' + store.gridZoom
    });
  }

  if (gridPattern.value.id === GRID_PATTERN_IDS.SQUARE_45) {
    valid45degPositions.value = calculateValid45degPosition()
    valid45degBitPositions.value = calculateValid45degBitPosition()
    optimized45degSnapping.buildOptimizedStructures()
  }
  if (gridPattern.value.id === GRID_PATTERN_IDS.HEX_SPACED) {
    validSpacedHexPositions.value = calculateValidSpacedHexPosition()
    validSpacedDiamondPositions.value = calculateValidSpacedDiamondPosition()
  }
  validateItemPositions()
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("keyup", handleKeyUp);
  if (candidateTimer) {
    clearTimeout(candidateTimer)
    candidateTimer = null
  }
  if (stageResizeObserver) {
    stageResizeObserver.disconnect();
    stageResizeObserver = null;
  }
  if (stageResizeTimer !== null) {
    clearTimeout(stageResizeTimer);
    stageResizeTimer = null;
  }
});
</script>
